import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BrandLoader } from '../../components/BrandLoader';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  updateEdge,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  Connection,
  Edge,
  Node,
  ReactFlowInstance,
  Panel,
  BackgroundVariant,
  MarkerType,
  getRectOfNodes,
  getTransformForBounds,
  NodeChange,
  NodePositionChange,
  EdgeChange,
  NodeMouseHandler,
  SelectionMode,
  ConnectionMode,
} from 'reactflow';
import { toPng, toSvg } from 'html-to-image';
import {
  layoutElements,
  getLayoutedElements,
  deriveHandles,
  hierarchicalLayout,
  toAbsolutePositions,
  getExportBounds,
} from '../../services/layoutService';
import { TechNode, GroupNode, StickyNoteNode, TextNode, ShapeNode, EdgeSettingsPanel, NodeSettingsPanel, EditorContextMenu, HelperLines, getHelperLines, alignNodes, distributeNodes, EDGE_TYPES, EdgeLabelProvider, detachChildrenOfRemovedGroups, planGroupDetach } from '../../features/diagrams';
import type { CtxTarget, CtxActions } from '../../features/diagrams';
import { ToolButton, SubBar } from '../../features/diagrams/toolbar';
import { ContextMenu, ContextMenuTrigger } from '../../components/ui/beui';
import type { AlignType, DistributeAxis } from '../../features/diagrams';
import { MermaidImportModal } from '../../components/ui';
import { Tooltip } from '../../components/ui/beui';
import { CommandPalette, KeyboardCheatSheet } from '../../components/common';
import { generateTechStackWithAI, reviseDiagramWithAI, isAIConfigured } from '../../services/aiService';
import { canvasToGeneratedStack } from '../../services/diagramSerializer';
import { mergeRevisedStack } from '../../services/aiRevise';
import { AIPanel } from '../../features/ai';
import { AIRunLifecycle } from '../../features/ai/runLifecycle';
import { useAIDock } from '../../components/shell/EditorLayout';
import type { ChatMessage, ChatTurnMode } from '../../features/ai';
import { parseMermaidToStack, MermaidParseError } from '../../services/mermaidParser';
import { validateAndCorrectStack, attemptAutoFix } from '../../services/stackValidation';
import type { AIProvider } from '../../services/apiKeyService';
import {
  getDiagram,
  createDiagram,
  updateDiagram,
  isAuthError,
} from '../../services/diagramService';
import {
  trackDiagramSaved,
  trackDiagramExported,
  trackDiagramImported,
  trackAIGenerationStarted,
  trackAIGenerationCompleted,
  trackAIGenerationFailed,
  trackAutoLayoutUsed,
} from '../../lib/posthog';
import { useAuth } from '../Auth/AuthProvider';
import { IconData, TechNodeData, GroupNodeData, StickyNoteData, TextNodeData, ShapeNodeData, ShapeType, SaveStatus, GeneratedStack, AIGenerationProgressEvent } from '../../types';
import { useUndoRedo } from '../../hooks/useUndoRedo';
import { useSubscription } from '../../hooks/useSubscription';
import { UpgradePrompt } from '../../features/billing';
import {
  Image as ImageIcon,
  FileJson,
  Save,
  Crown,
  Share2,
  Settings,
  ChevronDown,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignCenterHorizontal,
  AlignEndHorizontal,
  AlignHorizontalDistributeCenter,
  AlignVerticalDistributeCenter,
  Square,
  Circle,
  Triangle,
  Diamond,
  Hexagon,
  Star,
} from 'lucide-react';
// Editor toolbars use filled Phosphor glyphs; the canvas and node renderers
// stay on Lucide (see AGENTS.md).
import {
  ArrowCounterClockwise,
  ArrowClockwise,
  MagnifyingGlass,
  Sparkle,
  Plus,
  TreeStructure,
  Eye,
  TextT,
  Note,
  GridNine,
  Magnet,
  MapTrifold,
  ArrowsOut,
  Crosshair,
} from '@phosphor-icons/react';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import { ShareModal, OnboardingModal } from '../../components/ui';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../../components/ui/beui';
import { AuthModal } from '../Auth';
import { useTabs } from '../../context/TabsContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useSaveStatus } from '../../context/SaveStatusContext';
import {
  getShowMinimap,
  getShowGrid,
  getSnapToGrid,
  setShowMinimap as persistShowMinimap,
  setShowGrid as persistShowGrid,
  setSnapToGrid as persistSnapToGrid,
} from '../../lib/editorPrefs';
import { useOnboarding } from '../../context/OnboardingContext';

const nodeTypes = {
  techNode: TechNode,
  groupNode: GroupNode,
  stickyNote: StickyNoteNode,
  textNode: TextNode,
  shapeNode: ShapeNode,
};

const getId = () =>
  `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Pan with the middle mouse button (+ trackpad via panOnScroll). Left-drag on
// the pane is reserved for rubber-band selection (see selectionOnDrag), and the
// right button is left free for the right-click context menu.
const PAN_ON_DRAG: number[] = [1];

// Modifiers that add a clicked node to the current selection.
const MULTI_SELECT_KEYS = ['Meta', 'Control', 'Shift'];

// Keys that delete the current selection (nodes drop their connected edges too).
const DELETE_KEYS = ['Delete', 'Backspace'];

// The transient toolbars a persistent-bar trigger can slide in.
type SubBarId = 'insert' | 'layout' | 'view';

/** Restacking directions for the selected nodes. */
type ZOrder = 'front' | 'forward' | 'backward' | 'back';

/**
 * Descriptions used to default to the placeholder "Component" and were hidden
 * behind a per-node toggle. They now render whenever they're non-empty, so drop
 * the placeholder on load — otherwise every node in an existing diagram would
 * suddenly read "Component".
 */
const LEGACY_DESCRIPTION = 'Component';

const stripLegacyDescription = (data: Record<string, unknown>): Record<string, unknown> =>
  data.description === LEGACY_DESCRIPTION ? { ...data, description: undefined } : data;

// Shapes have no Lucide glyph, so they're drawn inline.
const ParallelogramIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" className={className}>
    <path d="M7 5h14l-4 14H3z" />
  </svg>
);

const CylinderIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <ellipse cx="12" cy="6" rx="7" ry="3" />
    <path d="M5 6v12c0 1.7 3.1 3 7 3s7-1.3 7-3V6" />
  </svg>
);

// The Insert ▸ Shapes submenu. Icon colors preview each shape's default fill.
const INSERT_SHAPES: { type: ShapeType; label: string; Icon: React.FC<{ className?: string }> }[] = [
  { type: 'square', label: 'Square', Icon: (p) => <Square {...p} /> },
  { type: 'circle', label: 'Circle', Icon: (p) => <Circle {...p} /> },
  { type: 'triangle', label: 'Triangle', Icon: (p) => <Triangle {...p} /> },
  { type: 'diamond', label: 'Diamond', Icon: (p) => <Diamond {...p} /> },
  { type: 'hexagon', label: 'Hexagon', Icon: (p) => <Hexagon {...p} /> },
  { type: 'star', label: 'Star', Icon: (p) => <Star {...p} /> },
  { type: 'parallelogram', label: 'Parallelogram', Icon: ParallelogramIcon },
  { type: 'cylinder', label: 'Cylinder', Icon: CylinderIcon },
];

const SHAPE_ICON_COLOR: Record<ShapeType, string> = {
  square: 'text-blue-500',
  circle: 'text-emerald-500',
  triangle: 'text-amber-500',
  diamond: 'text-violet-500',
  hexagon: 'text-pink-500',
  star: 'text-yellow-500',
  parallelogram: 'text-cyan-500',
  cylinder: 'text-indigo-500',
};

// True when a keyboard event originates from a text-input surface, so global
// canvas shortcuts (nudge, zoom, duplicate, delete, select-all, copy/paste)
// don't hijack typing in a field or modal.
const isTypingTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    el.isContentEditable === true
  );
};

const downloadFile = (dataUrl: string, filename: string) => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// --- LAYOUT ---
// Dagre and semantic (model-hint) layout live in services/layoutService.ts.


/**
 * Turn a GeneratedStack (from AI generation or Mermaid import) into positioned
 * React Flow nodes/edges. Append mode keeps existing nodes untouched, lays out
 * only the new cluster, and drops it below the current content; replace mode
 * lays out everything and wraps groups in groupNode containers.
 */
const materializeGeneratedStack = (
  stack: GeneratedStack,
  opts: {
    append: boolean;
    existingNodes: Node[];
    existingEdges: Edge[];
    direction?: 'TB' | 'BT' | 'LR' | 'RL';
    triggerSnapshot: () => void;
  }
): { nodes: Node[]; edges: Edge[]; newNodeCount: number; newEdgeCount: number } => {
  const { append: isAppendMode, existingNodes, existingEdges, triggerSnapshot } = opts;
  const direction = opts.direction ?? 'TB';

  // Generate unique IDs for new nodes to avoid conflicts
  const timestamp = Date.now();
  const newNodes: Node<TechNodeData>[] = stack.nodes.map((n, index) => {
    const nodeId = isAppendMode ? `ai_${timestamp}_${index}_${n.id}` : n.id;

    return {
      id: nodeId,
      type: 'techNode',
      position: { x: 0, y: 0 }, // Will be set by the layout service
      data: {
        label: n.label,
        // Use the new icon format directly
        icon: n.icon,
        // Keep iconSlug for backward compatibility
        iconSlug: n.icon?.startsWith('si:') ? n.icon.substring(3) : n.icon,
        description: n.description,
        category: n.category,
        importance: n.importance,
        groupId: n.groupId,
        metadata: n.metadata,
        // Semantic layout hints (layer/orderInLayer/mainPath) for the placer
        layout: n.layout,
        onSnapshot: triggerSnapshot,
        isAI: true,
      },
    };
  });

  // Create a mapping from old IDs to new IDs for edge connections
  const idMapping = new Map<string, string>();
  stack.nodes.forEach((n, index) => {
    idMapping.set(n.id, isAppendMode ? `ai_${timestamp}_${index}_${n.id}` : n.id);
  });

  const newEdges: Edge[] = stack.edges.map((e, i) => {
    // Map source/target to new IDs
    const mappedSource = idMapping.get(e.source) || e.source;
    const mappedTarget = idMapping.get(e.target) || e.target;

    // Determine edge style based on type
    const edgeStyles = {
      data: { stroke: '#3b82f6', strokeWidth: 2 },
      api: { stroke: '#10b981', strokeWidth: 2 },
      event: { stroke: '#f59e0b', strokeWidth: 2, strokeDasharray: '5 5' },
      dependency: { stroke: '#64748b', strokeWidth: 1 },
      control: { stroke: '#ef4444', strokeWidth: 2 },
    };

    const edgeStyle = edgeStyles[e.type as keyof typeof edgeStyles] || edgeStyles.data;

    return {
      id: `e_${timestamp}_${i}`,
      source: mappedSource,
      target: mappedTarget,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
      label: e.label,
      labelStyle: { fill: '#374151', fontSize: 12 },
      labelBgStyle: { fill: '#f9fafb', stroke: '#e5e7eb' },
      animated: e.type === 'event',
      // Orthogonal routing with rounded corners — keeps generated roadmaps and
      // engine layouts from turning into spaghetti. Hand-drawn edges are
      // unaffected; users can still switch any edge via the context menu.
      type: 'smoothstep',
      style: edgeStyle,
      markerEnd: { type: MarkerType.ArrowClosed, color: edgeStyle.stroke },
      markerStart: e.bidirectional ? { type: MarkerType.ArrowClosed, color: edgeStyle.stroke } : undefined,
    };
  });

  // Create sticky notes if present
  const newStickyNotes: Node[] = (stack.stickyNotes || []).map(note => {
    return {
      id: `note_${timestamp}_${note.id}`,
      type: 'stickyNote',
      position: { x: 100, y: 100 }, // Will be positioned relative
      data: {
        label: note.label || 'Note',
        color: note.color || 'yellow',
        onSnapshot: triggerSnapshot,
      },
      style: { width: 200, height: 160 }, // Default size to match manual sticky notes
    };
  });

  // Create text nodes if present
  const newTextNodes: Node[] = (stack.textNodes || []).map(textNode => ({
    id: `text_${timestamp}_${textNode.id}`,
    type: 'textNode',
    position: { x: 100, y: 100 }, // Will be positioned
    data: {
      label: textNode.label,
      description: textNode.description,
      onSnapshot: triggerSnapshot,
    },
  }));

  // Generated groups — map member ids through the same remapping applied to
  // nodes so they line up.
  const mappedGroups = (stack.groups || [])
    .filter((gr) => gr && gr.label && Array.isArray(gr.nodeIds) && gr.nodeIds.length > 0)
    .map((gr) => ({
      id: gr.id,
      label: gr.label,
      nodeIds: gr.nodeIds.map((id) => idMapping.get(id) || id),
    }));

  let finalNodes: Node[];
  let finalEdges: Edge[];

  if (isAppendMode) {
    // Append mode: keep every existing node exactly where it is (manual positions,
    // groups, and parenting all preserved). Lay out ONLY the new nodes, then drop
    // that cluster below the existing content so nothing overlaps or gets reflowed.
    const allNewNodes = [...newNodes, ...newStickyNotes, ...newTextNodes];
    // layoutElements prefers the model's semantic hints for the new cluster and
    // falls back to dagre when they're missing or unusable.
    const { nodes: laidNew } = layoutElements(allNewNodes, newEdges, [], direction);

    const nodeH = (n: Node) =>
      n.height ?? (typeof n.style?.height === 'number' ? n.style.height : 100);
    const topLevel = existingNodes.filter((n) => !n.parentNode);
    const existMinX = topLevel.length
      ? Math.min(...topLevel.map((n) => n.position.x))
      : 0;
    const existMaxY = topLevel.length
      ? Math.max(...topLevel.map((n) => n.position.y + nodeH(n)))
      : 0;

    // Left-align the new cluster with existing content and place it GAP below.
    const GAP = 80;
    const newMinX = laidNew.length ? Math.min(...laidNew.map((n) => n.position.x)) : 0;
    const newMinY = laidNew.length ? Math.min(...laidNew.map((n) => n.position.y)) : 0;
    const dx = existMinX - newMinX;
    const dy = topLevel.length ? existMaxY + GAP - newMinY : 0;

    const offsetNew = laidNew.map((n) => ({
      ...n,
      position: { x: n.position.x + dx, y: n.position.y + dy },
    }));

    finalNodes = [...existingNodes, ...offsetNew];
    finalEdges = [...existingEdges, ...newEdges];
  } else {
    // Replace mode: only use new nodes
    const allNewNodes = [...newNodes, ...newStickyNotes, ...newTextNodes];

    if (mappedGroups.length > 0) {
      try {
        // Each group becomes a React Flow parentNode sub-flow: layoutElements
        // emits a groupNode container per group and re-parents the members
        // (relative coordinates, extent: 'parent'). Semantic hints (when
        // usable) place the spine and fan ribs; dagre compound layout is the
        // fallback.
        const groupSpecs = mappedGroups.map((gr) => ({
          id: `group_${timestamp}_${gr.id}`,
          label: gr.label,
          nodeIds: gr.nodeIds,
        }));
        const { nodes: laid, edges: laidEdges } = layoutElements(
          allNewNodes,
          newEdges,
          groupSpecs,
          direction
        );

        // Container data comes from the layout service as { label } — inject
        // the editor's snapshot hook so group edits are undoable.
        finalNodes = laid.map((node) =>
          node.type === 'groupNode'
            ? { ...node, data: { ...node.data, onSnapshot: triggerSnapshot } }
            : node
        );
        finalEdges = laidEdges;
      } catch (err) {
        console.warn('Group layout failed; using flat layout', err);
        const { nodes: layoutedNodes, edges: layoutedEdges } =
          getLayoutedElements(allNewNodes, newEdges, direction);
        finalNodes = layoutedNodes;
        finalEdges = layoutedEdges;
      }
    } else {
      // No groups: same preference order — semantic hints, then dagre.
      const { nodes: layoutedNodes, edges: layoutedEdges } =
        layoutElements(allNewNodes, newEdges, [], direction);
      finalNodes = layoutedNodes;
      finalEdges = layoutedEdges;
    }
  }

  // Handles are derived from FINAL geometry — model/parser guesses are never
  // trusted ("automatic routes own their endpoint sides"). Existing user edges
  // (append mode) keep their handles; only the new edges are rewritten.
  const newEdgeIds = new Set(newEdges.map((e) => e.id));
  const edgesToDerive = finalEdges.filter((e) => newEdgeIds.has(e.id));
  const handleMap = deriveHandles(edgesToDerive, finalNodes, direction);
  if (handleMap.size > 0) {
    finalEdges = finalEdges.map((e) => {
      const h = handleMap.get(e.id);
      return h ? { ...e, sourceHandle: h.sourceHandle, targetHandle: h.targetHandle } : e;
    });
  }

  return {
    nodes: finalNodes,
    edges: finalEdges,
    newNodeCount: newNodes.length,
    newEdgeCount: newEdges.length,
  };
};

// Auto-save debounce delay in ms — save shortly after the user stops editing.
const AUTO_SAVE_DELAY = 1500;

interface FlowEditorProps {
  isDark: boolean;
  /** Unauthenticated public demo at /demo — same editor, no persistence. */
  demo?: boolean;
}

const FlowEditorContent: React.FC<FlowEditorProps> = ({
  isDark,
  demo = false,
}) => {
  const { diagramId } = useParams<{ diagramId?: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null);
  // The AI dock is owned by the editor shell (EditorLayout) so the whole
  // shell — top bar, tabs, canvas card — shrinks when the panel opens.
  // Outside the shell (/demo) fall back to local state and an inline dock.
  const aiDock = useAIDock();
  const [demoAIPanelOpen, setDemoAIPanelOpen] = useState(false);
  const isAIPanelOpen = aiDock ? aiDock.open : demoAIPanelOpen;
  const setIsAIPanelOpen = useMemo(
    () => (aiDock ? aiDock.setOpen : setDemoAIPanelOpen),
    [aiDock]
  );
  const [isMermaidModalOpen, setIsMermaidModalOpen] = useState(false);
  const [mermaidError, setMermaidError] = useState<string | null>(null);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  // The command palette doubles as the icon picker. In 'changeIcon' mode,
  // selecting an icon re-skins the target node instead of adding a new one.
  const [commandPaletteMode, setCommandPaletteMode] = useState<'add' | 'changeIcon'>('add');
  const [iconChangeNodeId, setIconChangeNodeId] = useState<string | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  // Right-click context menu target (canvas / node / edge / multi-selection).
  // Radix ContextMenu owns open/close; we only track *what* was right-clicked
  // and the screen point (for "paste here" / "add node here").
  const [ctxTarget, setCtxTarget] = useState<CtxTarget>({ type: 'pane' });
  const ctxPosRef = useRef({ x: 0, y: 0 });
  const pendingAddPosRef = useRef<{ x: number; y: number } | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const hasInitialized = useRef(false);

  // Cloud save state
  const [currentDiagramId, setCurrentDiagramId] = useState<string | null>(null);
  const [currentDiagramName, setCurrentDiagramName] = useState<string>('Untitled Diagram');
  const { openTab } = useTabs();
  const toast = useToast();
  const confirm = useConfirm();
  const [diagramIcon, setDiagramIcon] = useState<string | null>(null);
  const [diagramIconColor, setDiagramIconColor] = useState<string | null>(null);
  // Register/refresh this diagram as an open tab in the browser-style tab strip.
  // Keyed on the *loaded* diagram (currentDiagramId), not the URL param — the id
  // and name are set together on load, so a tab can never be labelled with the
  // previous diagram's name during a switch.
  useEffect(() => {
    if (currentDiagramId && currentDiagramName && currentDiagramName !== 'Untitled Diagram') {
      openTab({ id: currentDiagramId, name: currentDiagramName, icon: diagramIcon, iconColor: diagramIconColor });
    }
  }, [currentDiagramId, currentDiagramName, diagramIcon, diagramIconColor, openTab]);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(SaveStatus.IDLE);

  // Publish save state to the tab strip, which now owns the editor's chrome.
  // The demo never persists, so it reports nothing; clear on unmount so Home
  // and Settings don't show a stale "Saved".
  const { setSaveStatus: publishSaveStatus } = useSaveStatus();
  useEffect(() => {
    publishSaveStatus(demo ? null : saveStatus);
  }, [saveStatus, demo, publishSaveStatus]);
  useEffect(() => () => publishSaveStatus(null), [publishSaveStatus]);

  const [isModified, setIsModified] = useState(false);
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clipboardRef = useRef<Node[]>([]);

  // Undo/Redo Hook
  const { takeSnapshot, undo, redo, canUndo, canRedo, clearHistory } =
    useUndoRedo();

  // Subscription info for export gating
  const { canExportFormat } = useSubscription();
  const [showExportUpgrade, setShowExportUpgrade] = useState<'export-svg' | null>(null);

// Sharing state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [shareToken, setShareToken] = useState<string | null>(null);

  // Onboarding state
  const { currentStep, completeStep } = useOnboarding();
  const nodesCountRef = useRef(0);
  const edgesCountRef = useRef(0);

  // Arrow-key nudge: coalesce a burst of nudges into a single undo step.
  const nudgeSnapshotTaken = useRef(false);
  const nudgeResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // AI configuration state
  const [showAIConfigPrompt, setShowAIConfigPrompt] = useState(false);
  // Chat transcript for the docked AI panel — one assistant message per turn,
  // carrying its own live activity feed (supersedes the old modal-wide feed).
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const aiLifecycle = useMemo(() => new AIRunLifecycle(), []);
  
  // View preferences. Toggled from the toolbar's View menu (Settings writes the
  // same keys), persisted per browser so they survive a remount.
  const [showGrid, setShowGrid] = useState(getShowGrid);
  const [showMinimap, setShowMinimap] = useState(getShowMinimap);
  const [snapToGrid, setSnapToGrid] = useState(getSnapToGrid);

  useEffect(() => { persistShowGrid(showGrid); }, [showGrid]);
  useEffect(() => { persistShowMinimap(showMinimap); }, [showMinimap]);
  useEffect(() => { persistSnapToGrid(snapToGrid); }, [snapToGrid]);

  // Which sub-bar is slid in above the persistent bar. Only one slot exists —
  // an open sub-bar takes it, otherwise the selection's contextual bar does.
  const [openSubBar, setOpenSubBar] = useState<SubBarId | null>(null);
  const openSubBarRef = useRef<SubBarId | null>(null);
  useEffect(() => { openSubBarRef.current = openSubBar; }, [openSubBar]);

  // Pointer-downs inside this wrapper don't dismiss the sub-bar, so a trigger
  // click toggles instead of closing and immediately reopening.
  const toolbarsRef = useRef<HTMLDivElement>(null);
  const insertBtnRef = useRef<HTMLButtonElement>(null);
  const layoutBtnRef = useRef<HTMLButtonElement>(null);
  const viewBtnRef = useRef<HTMLButtonElement>(null);

  const toggleSubBar = useCallback(
    (id: SubBarId) => setOpenSubBar((cur) => (cur === id ? null : id)),
    [],
  );
  const closeSubBar = useCallback(() => setOpenSubBar(null), []);

  // Smart alignment guide lines (flow-space coords; undefined = hidden)
  const [helperLineHorizontal, setHelperLineHorizontal] = useState<number | undefined>(undefined);
  const [helperLineVertical, setHelperLineVertical] = useState<number | undefined>(undefined);

  // Keep refs to the latest nodes/edges. Snapshot callbacks get baked into
  // node `data` (which ReactFlow preserves by reference across edits), so a
  // closure over `nodes`/`edges` would go stale — reading via refs ensures an
  // inline node edit snapshots the *current* graph, not the graph as it was
  // when the node was created.
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  nodesRef.current = nodes;
  edgesRef.current = edges;

  // The diagram id currently loaded into the canvas (distinct from the URL
  // param). Used to detect tab switches so the editor reloads the new diagram.
  const loadedDiagramIdRef = useRef<string | null>(null);
  // Latest `isModified`, readable from effects/callbacks without re-subscribing.
  const isModifiedRef = useRef(false);
  isModifiedRef.current = isModified;
  // Latest diagram name, so auto-save reads the current value without churn.
  const currentDiagramNameRef = useRef('Untitled Diagram');
  currentDiagramNameRef.current = currentDiagramName;
  // Guards against overlapping auto-saves (esp. concurrent auto-create).
  const isSavingRef = useRef(false);
  // Cascade counter so successive palette-added nodes don't stack on each other.
  const addCascadeRef = useRef(0);

  // Flag the diagram as edited so auto-save picks it up. Called only from real
  // mutations — NOT from loading/measuring/selecting — so merely viewing or
  // switching to a diagram never triggers a save.
  const markDirty = useCallback(() => {
    aiLifecycle.markCanvasChanged();
    setIsModified(true);
    setSaveStatus(SaveStatus.UNSAVED);
  }, [aiLifecycle]);

  // Snapshot for undo + mark the diagram dirty. Every genuine edit path calls
  // this (add/delete/connect/drag-start/paste/group/layout/AI/color/nudge/inline
  // label…), so it's the single source of truth for "an edit happened".
  const triggerSnapshot = useCallback(() => {
    takeSnapshot(nodesRef.current, edgesRef.current);
    markDirty();
  }, [takeSnapshot, markDirty]);

  // --- AI chat panel ---
  // Toggle the shell-level AI dock. In demo mode the toolbar button is an
  // upsell and opens the auth modal; without an API key the config prompt
  // shows instead.
  const handleToggleAIPanel = useCallback(() => {
    if (demo) {
      setIsAuthModalOpen(true);
      return;
    }
    if (!isAIConfigured()) {
      setShowAIConfigPrompt(true);
      return;
    }
    setIsAIPanelOpen((open) => !open);
  }, [demo, setIsAIPanelOpen]);

  // Patch a single chat message (append activity events / settle the turn).
  const updateChatMessage = useCallback(
    (id: string, patch: (m: ChatMessage) => Partial<ChatMessage>) => {
      setChatMessages((msgs) =>
        msgs.map((m) => (m.id === id ? { ...m, ...patch(m) } : m))
      );
    },
    []
  );

  const cancelAIActivity = useCallback(() => {
    aiLifecycle.cancelRun();
    setIsGenerating(false);
    setChatMessages((msgs) =>
      msgs.map((m) =>
        m.role === 'assistant' && m.status === 'working' ? { ...m, status: 'aborted' } : m
      )
    );
  }, [aiLifecycle]);

  useEffect(() => () => aiLifecycle.cancelRun(), [aiLifecycle]);

  const handleAINewChat = useCallback(() => {
    cancelAIActivity();
    setChatMessages([]);
  }, [cancelAIActivity]);

  /**
   * One conversational AI turn. Empty canvas (or no AI-manageable nodes) →
   * initial generation + replace-mode materialize; otherwise → revise against
   * the serialized canvas + position-preserving merge (full replace when the
   * merge reports lowMatch). triggerSnapshot before the run keeps each turn a
   * single undo step.
   */
  const handleChatSend = useCallback(
    async (instruction: string, provider?: AIProvider, modelId?: string, retryMessageId?: string) => {
      cancelAIActivity();
      const assistantId =
        retryMessageId ??
        `msg_${Date.now()}_a_${Math.random().toString(36).substr(2, 9)}`;
      const run = aiLifecycle.startRun();
      const { signal } = run.controller;
      const updateTurn = (patch: (m: ChatMessage) => Partial<ChatMessage>) => {
        if (!aiLifecycle.isCurrentRun(run.id)) return;
        updateChatMessage(assistantId, patch);
      };

      if (retryMessageId) {
        // Retry: reset the failed turn in place — no duplicate user bubble.
        updateTurn(() => ({
          status: 'working',
          activity: [],
          quality: undefined,
          mergeCounts: undefined,
          mode: undefined,
          replyText: undefined,
          errorText: undefined,
          retryPayload: undefined,
          provider,
          modelId,
        }));
      } else {
        setChatMessages((msgs) => [
          ...msgs,
          {
            id: `msg_${Date.now()}_u_${Math.random().toString(36).substr(2, 9)}`,
            role: 'user',
            content: instruction,
          },
          {
            id: assistantId,
            role: 'assistant',
            content: '',
            status: 'working',
            activity: [],
            provider,
            modelId,
          },
        ]);
      }

      triggerSnapshot();
      const canvasRevision = aiLifecycle.getCanvasRevision();
      setIsGenerating(true);
      trackAIGenerationStarted(instruction.length);

      const onProgress = (event: AIGenerationProgressEvent) =>
        updateTurn((m) => ({
          activity: [...(m.activity ?? []), event],
        }));

      // Serialize the live canvas: the revise path hands the model the current
      // stack. A canvas with no AI-manageable nodes (e.g. only shapes) is
      // treated as empty — a revise prompt would have nothing to match.
      const currentStack = canvasToGeneratedStack(nodes, edges);
      const isInitial = nodes.length === 0 || currentStack.nodes.length === 0;

      try {
        const result = isInitial
          ? await generateTechStackWithAI(instruction, provider, undefined, { onProgress, signal, model: modelId })
          : await reviseDiagramWithAI(instruction, provider, currentStack, { onProgress, signal, model: modelId });

        if (result.aborted || signal.aborted) {
          updateTurn(() => ({ status: 'aborted' }));
          return;
        }

        if (!result.success) {
          const errorMessage = result.error?.message || 'Failed to generate diagram';
          updateTurn(() => ({
            status: 'error',
            errorText: errorMessage,
            retryPayload: { instruction, provider, modelId },
          }));
          trackAIGenerationFailed(errorMessage);
          return;
        }

        if (result.data) {
          if (!aiLifecycle.isCurrentRun(run.id)) return;
          if (aiLifecycle.hasCanvasChanged(canvasRevision)) {
            updateTurn(() => ({
              status: 'error',
              errorText: 'Your canvas changed while AI was working, so this result was not applied. Retry to use the latest canvas.',
              retryPayload: { instruction, provider, modelId },
            }));
            return;
          }

          const latestNodes = nodesRef.current;
          const latestEdges = edgesRef.current;
          let finalNodes: Node[];
          let finalEdges: Edge[];
          let mode: ChatTurnMode;
          let mergeCounts: NonNullable<ChatMessage['mergeCounts']>;

          if (isInitial) {
            const m = materializeGeneratedStack(result.data, {
              append: false,
              existingNodes: latestNodes,
              existingEdges: latestEdges,
              triggerSnapshot,
            });
            finalNodes = m.nodes;
            finalEdges = m.edges;
            mode = 'initial';
            mergeCounts = { added: m.newNodeCount, updated: 0, removed: 0 };
          } else {
            const merge = mergeRevisedStack(result.data, latestNodes, latestEdges);
            if (merge.lowMatch) {
              // The model rewrote instead of revising — full replace instead of
              // scrambling the user's layout.
              const m = materializeGeneratedStack(result.data, {
                append: false,
                existingNodes: latestNodes,
                existingEdges: latestEdges,
                triggerSnapshot,
              });
              finalNodes = m.nodes;
              finalEdges = m.edges;
              mode = 'replace';
              mergeCounts = { added: m.newNodeCount, updated: 0, removed: 0 };
            } else {
              // Merge results lack onSnapshot on newly materialized nodes —
              // inject it so inline edits stay undoable (materialize parity).
              finalNodes = merge.nodes.map((n) =>
                n.data?.onSnapshot
                  ? n
                  : { ...n, data: { ...n.data, onSnapshot: triggerSnapshot } }
              );
              finalEdges = merge.edges;
              mode = 'revise';
              mergeCounts = {
                added: merge.added,
                updated: merge.updated,
                removed: merge.removed,
                changed: merge.changed,
                edgesAdded: merge.edgesAdded,
                edgesRemoved: merge.edgesRemoved,
                groupsAdded: merge.groupsAdded,
                groupsRemoved: merge.groupsRemoved,
                groupsUpdated: merge.groupsUpdated,
                addedLabels: merge.addedLabels,
                removedLabels: merge.removedLabels,
              };
            }
          }

          setNodes(finalNodes);
          setEdges(finalEdges);
          trackAIGenerationCompleted(mergeCounts.added, result.data.edges.length);

          setTimeout(() => {
            if (!aiLifecycle.hasCanvasChanged(canvasRevision)) {
              reactFlowInstance?.fitView({ padding: 0.2, duration: 800 });
            }
          }, 100);

          updateTurn(() => ({
            status: 'complete',
            mode,
            mergeCounts,
            replyText: result.notes,
            quality: result.quality
              ? { score: result.quality.score, repairRounds: result.quality.repairRounds }
              : undefined,
          }));
        }
      } catch (error) {
        if (signal.aborted || !aiLifecycle.isCurrentRun(run.id)) {
          updateTurn(() => ({ status: 'aborted' }));
          return;
        }
        console.error('Failed to generate', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        updateTurn(() => ({
          status: 'error',
          errorText: errorMessage,
          retryPayload: { instruction, provider, modelId },
        }));
        trackAIGenerationFailed(errorMessage);
      } finally {
        if (aiLifecycle.finishRun(run.id)) setIsGenerating(false);
      }
    },
    [nodes, edges, setNodes, setEdges, triggerSnapshot, reactFlowInstance, updateChatMessage, aiLifecycle, cancelAIActivity]
  );

  // Inject onSnapshot to nodes
  const prepareNode = useCallback(
    (node: Node) => {
      const normalizedParentNode =
        (node as unknown as { parentNode?: string }).parentNode ??
        (node as unknown as { parentId?: string }).parentId;

      return {
        ...node,
        ...(normalizedParentNode ? { parentNode: normalizedParentNode } : {}),
        data: {
          ...node.data,
          onSnapshot: triggerSnapshot,
        },
      };
    },
    [triggerSnapshot]
  );

  // Load a saved diagram by id into the canvas — used on first mount AND when
  // switching tabs. Resets undo history + selection so nothing leaks between
  // diagrams. Marks loadedDiagramIdRef optimistically to guard concurrent loads.
  const loadDiagramById = useCallback(
    async (id: string) => {
      cancelAIActivity();
      aiLifecycle.markCanvasChanged();
      loadedDiagramIdRef.current = id;
      // Don't force the full-screen loader here: on the first mount the editor
      // already starts in the loading state, and on a tab switch we keep the
      // current canvas visible until the new diagram swaps in (feels instant).
      try {
        const diagram = await getDiagram(id);
        const hydratedNodes = diagram.nodes.map((node: Node) => ({
          ...node,
          data: { ...stripLegacyDescription(node.data), onSnapshot: triggerSnapshot },
        }));
        setNodes(hydratedNodes);
        setEdges(diagram.edges);
        setCurrentDiagramId(diagram.id);
        setCurrentDiagramName(diagram.name);
        setDiagramIcon(diagram.icon);
        setDiagramIconColor(diagram.iconColor);
        setIsPublic(diagram.isPublic ?? false);
        setShareToken(diagram.shareToken ?? null);
        setIsModified(false);
        setSelectedNodeId(null);
        setSelectedEdgeId(null);
        clearHistory();
        setSaveStatus(SaveStatus.SAVED);
        setTimeout(() => setSaveStatus(SaveStatus.IDLE), 2000);
      } catch (error) {
        console.error('Failed to load diagram:', error);
        loadedDiagramIdRef.current = null;
        if (isAuthError(error)) {
          setIsAuthModalOpen(true);
        } else {
          navigate('/editor', { replace: true });
        }
      } finally {
        setIsInitialLoading(false);
      }
    },
    [setNodes, setEdges, triggerSnapshot, clearHistory, navigate, cancelAIActivity, aiLifecycle]
  );

  // Reset to a fresh, unsaved blank canvas (e.g. navigating to /editor with no
  // id after a diagram was open).
  const resetToBlankDiagram = useCallback(() => {
    cancelAIActivity();
    aiLifecycle.markCanvasChanged();
    setNodes([]);
    setEdges([]);
    setCurrentDiagramId(null);
    setCurrentDiagramName('Untitled Diagram');
    setDiagramIcon(null);
    setDiagramIconColor(null);
    setIsPublic(false);
    setShareToken(null);
    loadedDiagramIdRef.current = null;
    setIsModified(false);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    clearHistory();
    setSaveStatus(SaveStatus.IDLE);
  }, [setNodes, setEdges, clearHistory, cancelAIActivity, aiLifecycle]);

  // Fire-and-forget flush of the current diagram before it's replaced or the
  // editor unmounts (tab switch / leaving the editor), so a pending debounced
  // save isn't dropped. No state updates → safe during unmount. Persists a
  // not-yet-saved diagram too (create), covering AI/import/template canvases.
  const flushPendingSave = useCallback(() => {
    if (demo || !isAuthenticated || !isModifiedRef.current) return;
    const id = loadedDiagramIdRef.current;
    const outgoingNodes = nodesRef.current;
    const outgoingEdges = edgesRef.current;
    if (!id && outgoingNodes.length === 0 && outgoingEdges.length === 0) return;
    const viewport = reactFlowInstance?.getViewport();
    if (id) {
      updateDiagram(id, {
        name: currentDiagramNameRef.current,
        nodes: outgoingNodes,
        edges: outgoingEdges,
        viewport,
      }).catch((e) => console.error('Flush save failed:', e));
    } else {
      createDiagram({
        name: currentDiagramNameRef.current || 'Untitled Diagram',
        nodes: outgoingNodes,
        edges: outgoingEdges,
        viewport,
        icon: diagramIcon,
        iconColor: diagramIconColor,
      }).catch((e) => console.error('Flush create failed:', e));
    }
  }, [demo, isAuthenticated, reactFlowInstance, diagramIcon, diagramIconColor]);

  // Consume a one-time sessionStorage handoff (AI / import / template) from Home.
  // Returns true if one was handled (so we skip the diagram load this run).
  const consumeSessionInit = useCallback((): boolean => {
    const aiGenerateOpen = sessionStorage.getItem('aiGenerateOpen');
    if (aiGenerateOpen) {
      sessionStorage.removeItem('aiGenerateOpen');
      setIsInitialLoading(false);
      setTimeout(() => {
        if (isAIConfigured()) {
          setIsAIPanelOpen(true);
        } else {
          setShowAIConfigPrompt(true);
        }
      }, 100);
      return true;
    }

    const mermaidImport = sessionStorage.getItem('mermaidImport');
    if (mermaidImport) {
      sessionStorage.removeItem('mermaidImport');
      setIsInitialLoading(false);
      setTimeout(() => {
        setMermaidError(null);
        setIsMermaidModalOpen(true);
      }, 100);
      return true;
    }

    const importedData = sessionStorage.getItem('importedDiagram');
    if (importedData) {
      sessionStorage.removeItem('importedDiagram');
      try {
        const flow = JSON.parse(importedData);
        if (flow) {
          const { nodes: loadedNodes = [], edges: loadedEdges = [] } = flow;
          setNodes(
            loadedNodes.map((node: Node) => ({
              ...node,
              data: { ...stripLegacyDescription(node.data), onSnapshot: triggerSnapshot },
            }))
          );
          setEdges(loadedEdges);
          setCurrentDiagramName('Imported Diagram');
          setIsModified(true);
          trackDiagramImported(loadedNodes.length, loadedEdges.length);
        }
      } catch (e) {
        console.error('Failed to parse imported diagram:', e);
      }
      setIsInitialLoading(false);
      return true;
    }

    const templateData = sessionStorage.getItem('selectedTemplate');
    if (templateData) {
      sessionStorage.removeItem('selectedTemplate');
      try {
        const template = JSON.parse(templateData);
        if (template) {
          const { name, nodes: templateNodes = [], edges: templateEdges = [] } = template;
          setNodes(
            templateNodes.map((node: Node) => ({
              ...node,
              data: { ...stripLegacyDescription(node.data), onSnapshot: triggerSnapshot },
            }))
          );
          setEdges(templateEdges);
          setCurrentDiagramName(name || 'From Template');
          setIsModified(true);
        }
      } catch (e) {
        console.error('Failed to parse template data:', e);
      }
      setIsInitialLoading(false);
      return true;
    }

    return false;
  }, [setNodes, setEdges, triggerSnapshot, setIsAIPanelOpen]);

  // Initialize on mount + react to diagram switches (tab-strip navigations).
  useEffect(() => {
    if (isAuthLoading) return;

    // Have a diagram id but not signed in → prompt login (retry after auth).
    if (diagramId && !isAuthenticated) {
      setIsAuthModalOpen(true);
      setIsInitialLoading(false);
      return;
    }

    const run = async () => {
      // One-time sessionStorage handoff (AI / import / template) on first mount.
      if (!hasInitialized.current) {
        hasInitialized.current = true;
        if (consumeSessionInit()) return;
      }

      if (diagramId && isAuthenticated) {
        // Load only when the URL points at a different diagram than the canvas.
        if (diagramId !== loadedDiagramIdRef.current) {
          flushPendingSave();
          await loadDiagramById(diagramId);
        } else {
          setIsInitialLoading(false);
        }
      } else if (!diagramId && loadedDiagramIdRef.current !== null) {
        // Left a loaded diagram for a blank /editor → fresh canvas.
        flushPendingSave();
        resetToBlankDiagram();
        setIsInitialLoading(false);
      } else {
        setIsInitialLoading(false);
      }
    };

    run();
  }, [
    diagramId,
    isAuthenticated,
    isAuthLoading,
    consumeSessionInit,
    loadDiagramById,
    resetToBlankDiagram,
    flushPendingSave,
  ]);

  // Flush a pending save when the editor unmounts (leaving /editor entirely).
  // A ref keeps the unmount cleanup stable so it fires exactly once.
  const flushPendingSaveRef = useRef(flushPendingSave);
  flushPendingSaveRef.current = flushPendingSave;
  useEffect(() => () => flushPendingSaveRef.current(), []);

  // (Modified state is now flagged explicitly by markDirty on real edits — see
  // triggerSnapshot, manual resize in onNodesChangeWithHistory, undo/redo, and
  // rename — instead of diffing on every render, so viewing never marks dirty.)

  // Onboarding step progression
  useEffect(() => {
    if (currentStep === 0 && nodes.length > nodesCountRef.current) {
      nodesCountRef.current = nodes.length;
      if (nodes.length >= 1) {
        completeStep(0);
      }
    }
    if (currentStep === 1 && edges.length > edgesCountRef.current) {
      edgesCountRef.current = edges.length;
      if (edges.length >= 1) {
        completeStep(1);
      }
    }
  }, [nodes.length, edges.length, currentStep, completeStep]);

  // The one save path: update if the diagram exists, else create it (persisting
  // AI/import/template canvases and any first edit). Reads live state via refs so
  // its identity stays stable across edits; an in-flight guard avoids double
  // auto-create when saves overlap.
  // Returns true if saved, false on error, null if there was nothing to do.
  const performAutoSave = useCallback(async (): Promise<boolean | null> => {
    if (demo || !isAuthenticated || isSavingRef.current) return null;
    const curNodes = nodesRef.current;
    const curEdges = edgesRef.current;
    const id = loadedDiagramIdRef.current;
    // Nothing worth persisting for a brand-new, still-empty canvas.
    if (!id && curNodes.length === 0 && curEdges.length === 0) return null;

    isSavingRef.current = true;
    setSaveStatus(SaveStatus.SAVING);
    try {
      const viewport = reactFlowInstance?.getViewport();
      if (id) {
        await updateDiagram(id, {
          name: currentDiagramNameRef.current,
          nodes: curNodes,
          edges: curEdges,
          viewport,
        });
      } else {
        const created = await createDiagram({
          name: currentDiagramNameRef.current || 'Untitled Diagram',
          nodes: curNodes,
          edges: curEdges,
          viewport,
          icon: diagramIcon,
          iconColor: diagramIconColor,
        });
        setCurrentDiagramId(created.id);
        loadedDiagramIdRef.current = created.id;
        trackDiagramSaved(created.id);
        openTab({ id: created.id, name: created.name, icon: diagramIcon, iconColor: diagramIconColor });
        navigate(`/editor/${created.id}`, { replace: true });
      }
      setIsModified(false);
      setSaveStatus(SaveStatus.SAVED);
      setTimeout(() => setSaveStatus(SaveStatus.IDLE), 2000);
      return true;
    } catch (error) {
      console.error('Auto-save failed:', error);
      setSaveStatus(SaveStatus.ERROR);
      if (isAuthError(error)) setIsAuthModalOpen(true);
      return false;
    } finally {
      isSavingRef.current = false;
    }
  }, [demo, isAuthenticated, reactFlowInstance, diagramIcon, diagramIconColor, openTab, navigate]);

  // Debounced auto-save: fire shortly after the user stops editing. Surface a
  // failure once (not on every 1.5s retry) so a broken save doesn't go unnoticed.
  const saveErrorToastedRef = useRef(false);
  useEffect(() => {
    if (demo || !isAuthenticated || !isModified) return;
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = setTimeout(() => {
      performAutoSave().then((result) => {
        if (result === false && !saveErrorToastedRef.current) {
          saveErrorToastedRef.current = true;
          toast.error("Couldn't save your changes — check your connection.");
        } else if (result === true) {
          saveErrorToastedRef.current = false;
        }
      });
    }, AUTO_SAVE_DELAY);
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, [demo, isAuthenticated, isModified, nodes, edges, performAutoSave, toast]);

  // Ctrl+S / cloud button — force an immediate save with explicit feedback.
  const handleCloudSave = useCallback(async () => {
    if (!isAuthenticated) {
      setIsAuthModalOpen(true);
      return;
    }
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    const result = await performAutoSave();
    if (result === true) toast.success('Saved');
    else if (result === false) toast.error("Couldn't save your changes.");
  }, [isAuthenticated, performAutoSave, toast]);

  const onConnect = useCallback(
    (params: Connection) => {
      // Prevent self-connections
      if (params.source === params.target) {
        return;
      }
      
      // Snapshot before connecting
      triggerSnapshot();
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            animated: false,
            style: { stroke: '#64748b', strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b' },
          },
          eds
        )
      );
    },
    [setEdges, triggerSnapshot]
  );

  // --- Edge reconnection: drag an endpoint onto a different node to reroute ---
  const edgeReconnectSuccessful = useRef(true);
  const onEdgeUpdateStart = useCallback(() => {
    edgeReconnectSuccessful.current = false;
  }, []);
  const onEdgeUpdate = useCallback(
    (oldEdge: Edge, newConnection: Connection) => {
      if (newConnection.source === newConnection.target) return; // no self-loops
      edgeReconnectSuccessful.current = true;
      triggerSnapshot();
      setEdges((els) => updateEdge(oldEdge, newConnection, els));
    },
    [setEdges, triggerSnapshot]
  );
  const onEdgeUpdateEnd = useCallback(
    (_event: MouseEvent | TouchEvent, edge: Edge) => {
      // Released in empty space → drop the edge (Figma/tldraw behaviour).
      if (!edgeReconnectSuccessful.current) {
        triggerSnapshot();
        setEdges((eds) => eds.filter((e) => e.id !== edge.id));
        setSelectedEdgeId(null);
      }
      edgeReconnectSuccessful.current = true;
    },
    [setEdges, triggerSnapshot]
  );

  // Intercept changes to detect deletions + drive smart alignment guides.
  const onNodesChangeWithHistory = useCallback(
    (changes: NodeChange[]) => {
      // If nodes are being removed, snapshot first (also marks dirty).
      const removeChanges = changes.filter((c) => c.type === 'remove');
      if (removeChanges.length > 0) {
        triggerSnapshot();
        // Delete/Backspace goes through React Flow's native removal, which
        // cascades to — and orphans the children of — deleted group
        // containers. Re-attach the scheduled descendants so a keyboard
        // delete behaves exactly like the context-menu delete.
        const detachPlan = planGroupDetach(
          nodesRef.current,
          new Set(removeChanges.map((c) => c.id))
        );
        if (detachPlan.size > 0) {
          // Children that survive the delete keep their edges; React Flow
          // only dropped them because the cascade scheduled the child too.
          const stillRemoved = new Set(removeChanges.map((c) => c.id));
          detachPlan.forEach((_node, id) => stillRemoved.delete(id));
          const keptEdges = edgesRef.current.filter(
            (e) =>
              !e.selected &&
              !stillRemoved.has(e.source) &&
              !stillRemoved.has(e.target)
          );
          setNodes((nds) => nds.map((n) => detachPlan.get(n.id) ?? n));
          setEdges((eds) => {
            const missing = keptEdges.filter((e) => !eds.some((other) => other.id === e.id));
            return missing.length > 0 ? [...eds, ...missing] : eds;
          });
          changes = changes.filter((c) => !(c.type === 'remove' && detachPlan.has(c.id)));
        }
      } else if (
        // Manual node resize (NodeResizer sets `resizing`); plain auto-measurement
        // of a freshly-loaded diagram does NOT, so viewing never marks dirty.
        changes.some((c) => c.type === 'dimensions' && c.resizing != null)
      ) {
        markDirty();
      }

      // Reset guides each change; recompute only while a single node is dragging.
      setHelperLineHorizontal(undefined);
      setHelperLineVertical(undefined);

      const dragChange =
        changes.length === 1 &&
        changes[0].type === 'position' &&
        (changes[0] as NodePositionChange).dragging &&
        (changes[0] as NodePositionChange).position
          ? (changes[0] as NodePositionChange)
          : null;

      if (dragChange) {
        const dragged = nodesRef.current.find((n) => n.id === dragChange.id);
        const parented =
          dragged &&
          ((dragged as unknown as { parentNode?: string }).parentNode ||
            (dragged as unknown as { parentId?: string }).parentId);
        // Only guide top-level nodes (child coords aren't comparable).
        if (!parented) {
          const lines = getHelperLines(dragChange, nodesRef.current);
          // Mutate the change's position in place to snap onto the guide.
          dragChange.position!.x = lines.snapPosition.x ?? dragChange.position!.x;
          dragChange.position!.y = lines.snapPosition.y ?? dragChange.position!.y;
          setHelperLineHorizontal(lines.horizontal);
          setHelperLineVertical(lines.vertical);
        }
      }

      onNodesChange(changes);
    },
    [onNodesChange, setNodes, setEdges, triggerSnapshot, markDirty]
  );

  // Clear alignment guides when a drag ends.
  const onNodeDragStop = useCallback(() => {
    setHelperLineHorizontal(undefined);
    setHelperLineVertical(undefined);
  }, []);

  // Align / distribute the current multi-selection.
  const handleAlign = useCallback(
    (type: AlignType) => {
      triggerSnapshot();
      setNodes((nds) => alignNodes(nds, type));
    },
    [setNodes, triggerSnapshot]
  );

  const handleDistribute = useCallback(
    (axis: DistributeAxis) => {
      triggerSnapshot();
      setNodes((nds) => distributeNodes(nds, axis));
    },
    [setNodes, triggerSnapshot]
  );

  const onEdgesChangeWithHistory = useCallback(
    (changes: EdgeChange[]) => {
      // If edges are being removed, snapshot first
      if (changes.some((c) => c.type === 'remove')) {
        triggerSnapshot();
      }
      onEdgesChange(changes);
    },
    [onEdgesChange, triggerSnapshot]
  );

  const onNodeDragStart = useCallback(() => {
    triggerSnapshot();
  }, [triggerSnapshot]);

  const onSelectionDragStart = useCallback(() => {
    triggerSnapshot();
  }, [triggerSnapshot]);

  // Edge Selection Handling
  const onEdgeClick = useCallback((_event: React.MouseEvent, edge: Edge) => {
    _event.stopPropagation();
    setSelectedEdgeId(edge.id);
    setSelectedNodeId(null);
  }, []);

  const onPaneClick = useCallback((event: React.MouseEvent) => {
    setSelectedEdgeId(null);
    setSelectedNodeId(null);
    // Deselect all nodes when clicking empty canvas, unless a multi-select
    // modifier (Shift / Ctrl / Cmd) is held.
    const additive = event.shiftKey || event.ctrlKey || event.metaKey;
    if (!additive) {
      setNodes((nds) => nds.map((n) => ({ ...n, selected: false })));
    }
  }, [setNodes]);

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedEdgeId(null);

    // Add-to-selection with Shift / Ctrl / Cmd (conventional multi-select)
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      // Toggle selection for this node
      setNodes((nds) =>
        nds.map((n) =>
          n.id === node.id ? { ...n, selected: !n.selected } : n
        )
      );
      setSelectedNodeId(null); // Clear settings panel on multi-select
    } else {
      // Normal click - select only this node, deselect others
      setNodes((nds) =>
        nds.map((n) =>
          n.id === node.id ? { ...n, selected: true } : { ...n, selected: false }
        )
      );
      // Show settings panel for techNode and textNode
      if (node.type === 'techNode' || node.type === 'textNode') {
        setSelectedNodeId(node.id);
      } else {
        setSelectedNodeId(null);
      }
    }
  }, [setNodes]);

  // Right-click handlers. We do NOT preventDefault here — the event bubbles to
  // the BEUI ContextMenuTrigger wrapping the canvas, which opens the menu and
  // suppresses the browser menu. These fire first (inner→outer), so ctxTarget is
  // set before the menu renders. ctxPosRef holds the screen point for paste/add.
  const onNodeContextMenu: NodeMouseHandler = useCallback((event, node) => {
    ctxPosRef.current = { x: event.clientX, y: event.clientY };
    setSelectedEdgeId(null);
    const selectedCount = nodes.filter((n) => n.selected).length;
    if (node.selected && selectedCount > 1) {
      setCtxTarget({ type: 'selection', count: selectedCount });
    } else {
      setNodes((nds) => nds.map((n) => ({ ...n, selected: n.id === node.id })));
      if (node.type === 'techNode' || node.type === 'textNode') setSelectedNodeId(node.id);
      setCtxTarget({ type: 'node', node });
    }
  }, [nodes, setNodes]);

  const onPaneContextMenu = useCallback((event: React.MouseEvent | MouseEvent) => {
    ctxPosRef.current = { x: event.clientX, y: event.clientY };
    setCtxTarget({ type: 'pane' });
  }, []);

  const onEdgeContextMenu = useCallback((event: React.MouseEvent, edge: Edge) => {
    ctxPosRef.current = { x: event.clientX, y: event.clientY };
    setSelectedEdgeId(edge.id);
    setSelectedNodeId(null);
    setNodes((nds) => (nds.some((n) => n.selected) ? nds.map((n) => ({ ...n, selected: false })) : nds));
    setCtxTarget({ type: 'edge', edge });
  }, [setNodes]);

  const onSelectionContextMenu = useCallback((event: React.MouseEvent, selNodes: Node[]) => {
    ctxPosRef.current = { x: event.clientX, y: event.clientY };
    setCtxTarget({ type: 'selection', count: selNodes.length });
  }, []);

  // Edge Updates
  const handleEdgeUpdate = useCallback(
    (edgeId: string, updates: Partial<Edge>) => {
      triggerSnapshot();
      setEdges((eds) =>
        eds.map((e) => {
          if (e.id === edgeId) {
            return { ...e, ...updates };
          }
          return e;
        })
      );
    },
    [setEdges, triggerSnapshot]
  );

  const handleEdgeDelete = useCallback(
    (edgeId: string) => {
      triggerSnapshot();
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
      setSelectedEdgeId(null);
    },
    [setEdges, triggerSnapshot]
  );

  const selectedEdge = edges.find((e) => e.id === selectedEdgeId) || null;

  // Node Updates (for NodeSettingsPanel)
  const handleNodeUpdate = useCallback(
    (nodeId: string, updates: Partial<Node>) => {
      triggerSnapshot();
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === nodeId) {
            return { ...n, ...updates, data: { ...n.data, ...updates.data } };
          }
          return n;
        })
      );
    },
    [setNodes, triggerSnapshot]
  );

  const handleNodeDelete = useCallback(
    (nodeId: string) => {
      triggerSnapshot();
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setSelectedNodeId(null);
    },
    [setNodes, setEdges, triggerSnapshot]
  );

  const selectedNode = nodes.find((n) => n.id === selectedNodeId && (n.type === 'techNode' || n.type === 'textNode')) || null;

  const handleUndo = useCallback(() => {
    const result = undo(nodes, edges);
    if (result) {
      setNodes(result.nodes.map(prepareNode));
      setEdges(result.edges);
      setSelectedEdgeId(null);
      setSelectedNodeId(null);
      markDirty();
    }
  }, [undo, nodes, edges, setNodes, setEdges, prepareNode, markDirty]);

  const handleRedo = useCallback(() => {
    const result = redo(nodes, edges);
    if (result) {
      setNodes(result.nodes.map(prepareNode));
      setEdges(result.edges);
      setSelectedEdgeId(null);
      setSelectedNodeId(null);
      markDirty();
    }
  }, [redo, nodes, edges, setNodes, setEdges, prepareNode, markDirty]);

  const handleLayout = useCallback(
    (direction: 'TB' | 'LR' = 'TB') => {
      triggerSnapshot();
      // Ungrouped canvases use the hierarchy-aware layered pass (ranks from
      // edges, authored layer hints pin the ranks). Grouped canvases flatten
      // their sub-flows first (containers out, member coords absolute) and
      // re-layout through the orchestrator, which re-emits every group as a
      // fresh parentNode container — arranging a grouped canvas used to hand
      // parented children absolute coordinates, double-offsetting them.
      const containers = nodes.filter((n) => n.type === 'groupNode');
      const groupSpecs = containers
        .map((g) => ({
          id: g.id,
          label: (g.data as GroupNodeData | undefined)?.label,
          nodeIds: nodes.filter((c) => c.parentNode === g.id).map((c) => c.id),
        }))
        .filter((g) => g.nodeIds.length > 0);

      let layoutedNodes: Node[];
      let layoutedEdges: Edge[];
      if (groupSpecs.length > 0) {
        // Flatten BEFORE dropping the containers: members resolve their
        // absolute coordinates through the parent chain, so the containers
        // must still be present when toAbsolutePositions walks it.
        const flattened = toAbsolutePositions(nodes).filter(
          (n) => n.type !== 'groupNode'
        );
        const laid = layoutElements(flattened, edges, groupSpecs, direction);
        const prevData = new Map(
          containers.map((g) => [g.id, (g.data ?? {}) as GroupNodeData])
        );
        // Empty groups (no members) keep their containers where they were.
        const emptied = containers.filter((g) => !groupSpecs.some((s) => s.id === g.id));
        layoutedNodes = [
          ...emptied,
          ...laid.nodes.map((n) => {
            const prev = n.type === 'groupNode' ? prevData.get(n.id) : undefined;
            if (!prev) return n;
            return {
              ...n,
              data: { ...n.data, locked: prev.locked, onSnapshot: triggerSnapshot },
            };
          }),
        ];
        layoutedEdges = laid.edges;
      } else {
        const laid = hierarchicalLayout(nodes, edges, [], { direction });
        layoutedNodes = laid.nodes;
        layoutedEdges = laid.edges;
      }

      setNodes([...layoutedNodes]);
      setEdges([...layoutedEdges]);
      trackAutoLayoutUsed(direction);

      setTimeout(() => {
        reactFlowInstance?.fitView({ duration: 800 });
      }, 10);
    },
    [nodes, edges, setNodes, setEdges, triggerSnapshot, reactFlowInstance]
  );

  // --- Add Node Logic (via Command Palette) ---
  const handleAddNode = useCallback(
    (iconData: IconData) => {
      if (!reactFlowInstance || !reactFlowWrapper.current) return;

      triggerSnapshot();

      // If invoked from a canvas right-click ("Add node"), drop it where the
      // user clicked; otherwise cascade diagonally from viewport center so rapid
      // adds don't stack on top of each other (predictable, not random).
      let position;
      if (pendingAddPosRef.current) {
        position = reactFlowInstance.screenToFlowPosition(pendingAddPosRef.current);
        pendingAddPosRef.current = null;
      } else {
        const { width, height } = reactFlowWrapper.current.getBoundingClientRect();
        const step = addCascadeRef.current % 6;
        addCascadeRef.current += 1;
        position = reactFlowInstance.screenToFlowPosition({
          x: width / 2 + step * 26,
          y: height / 2 + step * 26,
        });
      }

      const newNode: Node<TechNodeData> = {
        id: getId(),
        type: 'techNode',
        position,
        data: {
          label: iconData.title,
          icon: iconData.icon,
          hex: iconData.hex,
          onSnapshot: triggerSnapshot,
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes, triggerSnapshot]
  );

  // Open the command palette in icon-picker mode for a specific tech node.
  const openIconPicker = useCallback((nodeId: string) => {
    setIconChangeNodeId(nodeId);
    setCommandPaletteMode('changeIcon');
    setIsCommandPaletteOpen(true);
  }, []);

  // Clone the in-app clipboard (nodes + their internal edges). With a target
  // flow position (canvas "Paste here") the group's top-left lands there;
  // otherwise a simple diagonal nudge (Ctrl/Cmd+V).
  const pasteClipboard = useCallback((atFlowPos?: { x: number; y: number }) => {
    const clip = clipboardRef.current;
    if (clip.length === 0) return;
    triggerSnapshot();
    let dx = 40;
    let dy = 40;
    if (atFlowPos) {
      const minX = Math.min(...clip.map((n) => n.position.x));
      const minY = Math.min(...clip.map((n) => n.position.y));
      dx = atFlowPos.x - minX;
      dy = atFlowPos.y - minY;
    }
    const idMap = new Map<string, string>();
    const stamp = Date.now();
    const pastedNodes = clip.map((n, i) => {
      const newId = `${n.type || 'node'}-${stamp}-${i}`;
      idMap.set(n.id, newId);
      return {
        ...n,
        id: newId,
        position: { x: n.position.x + dx, y: n.position.y + dy },
        selected: true,
        data: { ...n.data, onSnapshot: triggerSnapshot },
      };
    });
    const pastedEdges = edgesRef.current
      .filter((ed) => idMap.has(ed.source) && idMap.has(ed.target))
      .map((ed, i) => ({
        ...ed,
        id: `e-${stamp}-${i}`,
        source: idMap.get(ed.source)!,
        target: idMap.get(ed.target)!,
        selected: false,
      }));
    setNodes((nds) => [...nds.map((n) => ({ ...n, selected: false })), ...pastedNodes]);
    if (pastedEdges.length > 0) setEdges((eds) => [...eds, ...pastedEdges]);
  }, [setNodes, setEdges, triggerSnapshot]);

  // Swap a tech node's icon (and its brand color) to the picked one.
  const handleChangeNodeIcon = useCallback(
    (nodeId: string, icon: IconData) => {
      triggerSnapshot();
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId
            ? {
                ...n,
                data: { ...n.data, icon: icon.icon, hex: icon.hex, iconSlug: undefined },
              }
            : n
        )
      );
    },
    [setNodes, triggerSnapshot]
  );

  // Reset the palette back to add-node mode whenever it closes (any path).
  useEffect(() => {
    if (!isCommandPaletteOpen) {
      setCommandPaletteMode('add');
      setIconChangeNodeId(null);
    }
  }, [isCommandPaletteOpen]);

  const handleAddStickyNote = useCallback(() => {
    if (!reactFlowInstance || !reactFlowWrapper.current) return;
    triggerSnapshot();

    const { width, height } = reactFlowWrapper.current.getBoundingClientRect();
    const position = reactFlowInstance.screenToFlowPosition({
      x: width / 2 + (Math.random() * 40 - 20),
      y: height / 2 + (Math.random() * 40 - 20),
    });

    const newNode: Node<StickyNoteData> = {
      id: getId(),
      type: 'stickyNote',
      position,
      data: {
        label: '',
        color: 'yellow',
        onSnapshot: triggerSnapshot,
      },
      style: { width: 200, height: 160 }, // Default size
    };

    setNodes((nds) => nds.concat(newNode));
  }, [reactFlowInstance, setNodes, triggerSnapshot]);

  const handleAddTextNode = useCallback(() => {
    if (!reactFlowInstance || !reactFlowWrapper.current) return;
    triggerSnapshot();

    const { width, height } = reactFlowWrapper.current.getBoundingClientRect();
    const position = reactFlowInstance.screenToFlowPosition({
      x: width / 2 + (Math.random() * 40 - 20),
      y: height / 2 + (Math.random() * 40 - 20),
    });

    const newNode: Node<TextNodeData> = {
      id: getId(),
      type: 'textNode',
      position,
      data: {
        label: 'Text',
        description: '',
        onSnapshot: triggerSnapshot,
      },
    };

    setNodes((nds) => nds.concat(newNode));
  }, [reactFlowInstance, setNodes, triggerSnapshot]);

  const handleAddShapeNode = useCallback((shape: ShapeType) => {
    if (!reactFlowInstance || !reactFlowWrapper.current) return;
    triggerSnapshot();

    const { width, height } = reactFlowWrapper.current.getBoundingClientRect();
    const position = reactFlowInstance.screenToFlowPosition({
      x: width / 2 + (Math.random() * 40 - 20),
      y: height / 2 + (Math.random() * 40 - 20),
    });

    // Default dimensions per shape (NodeResizer lets the user change them after).
    const shapeSize: Record<string, { width: number; height: number }> = {
      square: { width: 96, height: 96 },
      circle: { width: 96, height: 96 },
      triangle: { width: 112, height: 96 },
      diamond: { width: 96, height: 96 },
      hexagon: { width: 112, height: 96 },
      star: { width: 112, height: 112 },
      parallelogram: { width: 128, height: 80 },
      cylinder: { width: 96, height: 112 },
    };

    const newNode: Node<ShapeNodeData> = {
      id: getId(),
      type: 'shapeNode',
      position,
      style: shapeSize[shape] ?? { width: 96, height: 96 },
      data: {
        label: shape.charAt(0).toUpperCase() + shape.slice(1),
        shape,
        onSnapshot: triggerSnapshot,
      },
    };

    setNodes((nds) => nds.concat(newNode));
  }, [reactFlowInstance, setNodes, triggerSnapshot]);

  // Duplicate all currently-selected nodes (+ edges internal to the selection),
  // offset slightly, and select the copies. Backs Ctrl/Cmd+D.
  /**
   * Restack the selection. Group containers are never targets — they're pinned
   * behind their children — but they do count toward the bounds, so a backdrop
   * shape sent to the back lands behind them too.
   */
  const handleReorder = useCallback(
    (direction: ZOrder, explicitNodeId?: string) => {
      // Right-clicking a node doesn't select it, so the context menu passes its
      // target explicitly. Otherwise act on the selection.
      const isTarget = (n: Node) =>
        n.type !== 'groupNode' && (explicitNodeId ? n.id === explicitNodeId : n.selected);

      if (!nodesRef.current.some(isTarget)) return;
      triggerSnapshot();

      setNodes((nds) => {
        const zOf = (n: Node) => (typeof n.zIndex === 'number' ? n.zIndex : 0);
        const all = nds.map(zOf);
        const maxZ = Math.max(0, ...all);
        const minZ = Math.min(0, ...all);

        return nds.map((n) => {
          if (!isTarget(n)) return n;
          const current = zOf(n);
          const next =
            direction === 'front' ? maxZ + 1
              : direction === 'back' ? minZ - 1
                : direction === 'forward' ? current + 1
                  : current - 1;
          return next === current ? n : { ...n, zIndex: next };
        });
      });
    },
    [setNodes, triggerSnapshot],
  );

  const handleDuplicateSelected = useCallback(() => {
    const sel = nodesRef.current.filter((n) => n.selected);
    if (sel.length === 0) return;
    triggerSnapshot();
    const idMap = new Map<string, string>();
    const stamp = Date.now();
    const dupNodes = sel.map((n, i) => {
      const newId = `${n.type || 'node'}-dup-${stamp}-${i}`;
      idMap.set(n.id, newId);
      return {
        ...n,
        id: newId,
        position: { x: n.position.x + 32, y: n.position.y + 32 },
        selected: true,
        data: { ...n.data, onSnapshot: triggerSnapshot },
      };
    });
    const dupEdges = edgesRef.current
      .filter((e) => idMap.has(e.source) && idMap.has(e.target))
      .map((e, i) => ({
        ...e,
        id: `e-dup-${stamp}-${i}`,
        source: idMap.get(e.source)!,
        target: idMap.get(e.target)!,
        selected: false,
      }));
    setNodes((nds) => [...nds.map((n) => ({ ...n, selected: false })), ...dupNodes]);
    if (dupEdges.length > 0) setEdges((eds) => [...eds, ...dupEdges]);
  }, [setNodes, setEdges, triggerSnapshot]);

  // Move every selected (draggable) node by (dx, dy). Backs arrow-key nudging.
  const nudgeSelected = useCallback(
    (dx: number, dy: number) => {
      setNodes((nds) => {
        if (!nds.some((n) => n.selected)) return nds;
        return nds.map((n) =>
          n.selected && n.draggable !== false
            ? { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } }
            : n
        );
      });
    },
    [setNodes]
  );

  // --- Keyboard Shortcuts ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      // Normalize letter keys: with Shift/Caps held, e.key is uppercase
      // (e.g. Ctrl+Shift+Z reports 'Z'), which would miss a bare 'z' compare.
      const key = e.key.toLowerCase();

      // Command Palette: Ctrl/Cmd+K (always active, even from a field)
      if (mod && key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen((prev) => !prev);
        return;
      }

      // Skip other shortcuts while the command palette (add-node or change-icon
      // mode) is open — it owns the keyboard.
      if (isCommandPaletteOpen) return;

      // Save: Ctrl/Cmd+S (allowed even while a field is focused, e.g. name input)
      if (mod && key === 's') {
        e.preventDefault();
        handleCloudSave();
        return;
      }

      // AI panel: Ctrl/Cmd+J — toggles even while typing in the panel composer.
      if (mod && key === 'j') {
        e.preventDefault();
        handleToggleAIPanel();
        return;
      }

      // Everything below must not fire while typing in an input / textarea / modal.
      if (isTypingTarget(e.target)) return;

      // Undo / Redo: Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z
      if (mod && key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          if (canRedo) handleRedo();
        } else if (canUndo) {
          handleUndo();
        }
        return;
      }
      // Redo alias: Ctrl/Cmd+Y
      if (mod && key === 'y') {
        e.preventDefault();
        if (canRedo) handleRedo();
        return;
      }
      // Duplicate: Ctrl/Cmd+D
      if (mod && key === 'd') {
        e.preventDefault();
        handleDuplicateSelected();
        return;
      }
      // Restack: Ctrl/Cmd+] / [ moves one step, adding Shift jumps to the end.
      // Read e.key directly — `key` is lowercased, which is harmless for
      // brackets, but Shift doesn't alter them so both forms arrive the same.
      if (mod && (e.key === ']' || e.key === '[')) {
        e.preventDefault();
        const forward = e.key === ']';
        handleReorder(e.shiftKey ? (forward ? 'front' : 'back') : forward ? 'forward' : 'backward');
        return;
      }
      // Select All: Ctrl/Cmd+A
      if (mod && key === 'a') {
        e.preventDefault();
        setNodes((nds) => nds.map((node) => ({ ...node, selected: true })));
        return;
      }
      // Copy: Ctrl/Cmd+C — stash the selected nodes in an in-app clipboard
      if (mod && key === 'c') {
        const sel = nodesRef.current.filter((n) => n.selected);
        if (sel.length > 0) {
          clipboardRef.current = sel;
          toast.success(`Copied ${sel.length} node${sel.length > 1 ? 's' : ''}`);
        }
        return;
      }
      // Paste: Ctrl/Cmd+V — clone the clipboard nodes (+ internal edges) with an offset
      if (mod && key === 'v') {
        if (clipboardRef.current.length > 0) {
          e.preventDefault();
          pasteClipboard();
        }
        return;
      }
      // Delete / Backspace are handled natively via <ReactFlow deleteKeyCode>,
      // which also removes edges connected to a deleted node. The 'remove'
      // changes flow through onNodes/EdgesChangeWithHistory (which snapshot).

      // Deselect / dismiss with Escape. An open sub-bar swallows the first press
      // (SubBar handles it directly when focus is inside it).
      if (e.key === 'Escape') {
        if (openSubBarRef.current) {
          setOpenSubBar(null);
          return;
        }
        setSelectedEdgeId(null);
        setSelectedNodeId(null);
        setNodes((nds) =>
          nds.some((n) => n.selected) ? nds.map((n) => ({ ...n, selected: false })) : nds
        );
        return;
      }
      // Zoom: + / - / 0 (fit) — 0 and Ctrl+0 both reset to fit
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        reactFlowInstance?.zoomIn({ duration: 150 });
        return;
      }
      if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        reactFlowInstance?.zoomOut({ duration: 150 });
        return;
      }
      if (e.key === '0') {
        e.preventDefault();
        reactFlowInstance?.fitView({ duration: 300 });
        return;
      }
      // Nudge selected nodes with arrow keys (Shift = 10px steps)
      if (
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight'
      ) {
        if (!nodesRef.current.some((n) => n.selected)) return;
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        // Coalesce a burst of nudges into a single undo step.
        if (!nudgeSnapshotTaken.current) {
          triggerSnapshot();
          nudgeSnapshotTaken.current = true;
        }
        if (nudgeResetTimer.current) clearTimeout(nudgeResetTimer.current);
        nudgeResetTimer.current = setTimeout(() => {
          nudgeSnapshotTaken.current = false;
        }, 500);
        nudgeSelected(dx, dy);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    handleUndo,
    handleRedo,
    canUndo,
    canRedo,
    handleCloudSave,
    handleDuplicateSelected,
    handleReorder,
    pasteClipboard,
    nudgeSelected,
    setNodes,
    setEdges,
    triggerSnapshot,
    isCommandPaletteOpen,
    reactFlowInstance,
    toast,
    handleToggleAIPanel,
  ]);

  const handleImportMermaid = (code: string) => {
    try {
      const { stack, direction, warnings } = parseMermaidToStack(code);
      const validation = validateAndCorrectStack(stack);
      if (!validation.isValid) {
        setMermaidError(validation.errors[0] || 'Could not import that diagram.');
        return;
      }
      // attemptAutoFix normalizes icons/importance. Connection handles are NOT
      // set here — materializeGeneratedStack derives them from final geometry.
      const fixed = attemptAutoFix(validation.correctedStack ?? stack);

      triggerSnapshot();
      const {
        nodes: finalNodes,
        edges: finalEdges,
        newNodeCount,
        newEdgeCount,
      } = materializeGeneratedStack(fixed, {
        append: nodes.length > 0,
        existingNodes: nodes,
        existingEdges: edges,
        direction,
        triggerSnapshot,
      });

      setNodes(finalNodes);
      setEdges(finalEdges);
      setMermaidError(null);
      setIsMermaidModalOpen(false);
      trackDiagramImported(newNodeCount, newEdgeCount);

      if (warnings.length > 0) {
        toast.info(
          `Imported with ${warnings.length} warning${warnings.length === 1 ? '' : 's'}: ${warnings[0]}${warnings.length > 1 ? '…' : ''}`
        );
      } else {
        toast.success('Mermaid diagram imported');
      }

      setTimeout(() => {
        reactFlowInstance?.fitView({ padding: 0.2, duration: 800 });
      }, 100);
    } catch (error) {
      setMermaidError(
        error instanceof MermaidParseError
          ? error.message
          : 'Could not parse that Mermaid code.'
      );
    }
  };

  const clearCanvas = async () => {
    const ok = await confirm({
      title: 'Clear canvas?',
      description: 'This removes every node and edge from the canvas and can’t be undone.',
      confirmText: 'Clear canvas',
      destructive: true,
    });
    if (!ok) return;
    triggerSnapshot();
    setNodes([]);
    setEdges([]);
    setSelectedEdgeId(null);
    setSelectedNodeId(null);
    clearHistory();
  };

  const handleGroupSelected = useCallback(() => {
    const selectedNodes = nodes.filter(
      (n) =>
        n.selected &&
        n.type !== 'groupNode' &&
        !(n as unknown as { parentNode?: string }).parentNode &&
        !(n as unknown as { parentId?: string }).parentId
    );

    if (selectedNodes.length === 0) {
      toast.info('Select at least one top-level node to group.');
      return;
    }

    triggerSnapshot();

    const rect = getRectOfNodes(selectedNodes);
    const groupId = getId();

    const groupNode: Node<GroupNodeData> = {
      id: groupId,
      type: 'groupNode',
      position: { x: rect.x - 20, y: rect.y - 40 },
      selected: true,
      zIndex: -1,
      style: {
        width: rect.width + 40,
        height: rect.height + 60,
      },
      data: { label: 'New Group', onSnapshot: triggerSnapshot },
    };

    const updatedNodes = nodes.map((node) => {
      if (
        node.selected &&
        node.type !== 'groupNode' &&
        !(node as unknown as { parentNode?: string }).parentNode &&
        !(node as unknown as { parentId?: string }).parentId
      ) {
        return {
          ...node,
          selected: false,
          parentNode: groupId,
          parentId: undefined,
          extent: 'parent' as const,
          position: {
            x: node.position.x - groupNode.position.x,
            y: node.position.y - groupNode.position.y,
          },
        };
      }
      return node;
    });

    setNodes([groupNode, ...updatedNodes]);
  }, [nodes, setNodes, triggerSnapshot, toast]);

  const handleUngroup = useCallback(
    (groupId: string) => {
      triggerSnapshot();

      setNodes((nds) => {
        const group = nds.find((n) => n.id === groupId);
        if (!group || group.type !== 'groupNode') return nds;

        const groupPosition = group.position;

        const detached = nds
          .map((n) => {
            const parentNode =
              (n as unknown as { parentNode?: string }).parentNode ??
              (n as unknown as { parentId?: string }).parentId;

            if (parentNode !== groupId) return n;

            return {
              ...n,
              parentNode: undefined,
              parentId: undefined,
              extent: undefined,
              position: {
                x: groupPosition.x + n.position.x,
                y: groupPosition.y + n.position.y,
              },
            };
          })
          .filter((n) => n.id !== groupId);

        return detached;
      });
    },
    [setNodes, triggerSnapshot]
  );

  const handleExport = async (format: 'png' | 'svg') => {
    await performExport(format);
  };

  const performExport = async (format: 'png' | 'svg') => {
    if (!reactFlowWrapper.current || nodes.length === 0) return;

    const viewport = reactFlowWrapper.current.querySelector(
      '.react-flow__viewport'
    ) as HTMLElement;
    const nodesBounds = getExportBounds(nodes);
    const transform = getTransformForBounds(
      nodesBounds,
      nodesBounds.width,
      nodesBounds.height,
      0.5,
      2
    );

    const options = {
      backgroundColor: isDark ? '#0f172a' : '#f8fafc',
      width: nodesBounds.width + 100,
      height: nodesBounds.height + 100,
      style: {
        width: `${nodesBounds.width}px`,
        height: `${nodesBounds.height}px`,
        transform: `translate(${transform[0]}px, ${transform[1]}px) scale(${transform[2]})`,
      },
    };

    try {
      let dataUrl;
      if (format === 'png') {
        dataUrl = await toPng(viewport, options);
      } else {
        dataUrl = await toSvg(viewport, options);
      }
      // Use the diagram name as the file name
      const fileName = `${currentDiagramName}.${format}`;
      downloadFile(dataUrl, fileName);
      trackDiagramExported(format);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (err) {
      console.error('Export failed', err);
      toast.error('Export failed. Please try again.');
    }
  };

  const handleSaveLocal = () => {
    performSaveLocal();
  };

  const performSaveLocal = () => {
    if (!reactFlowInstance) return;

    const flow = reactFlowInstance.toObject();
    const dataStr = JSON.stringify(flow, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    // Use the diagram name as the file name
    downloadFile(url, `${currentDiagramName}.json`);
    trackDiagramExported('json');
    toast.success('Exported as JSON');
  };

  const handleLoadLocal = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const flow = JSON.parse(e.target?.result as string);
        if (flow) {
          triggerSnapshot();
          const { nodes: loadedNodes = [], edges: loadedEdges = [] } = flow;
          const hydratedNodes = loadedNodes.map(prepareNode);
          setNodes(hydratedNodes);
          setEdges(loadedEdges);
          
          // Treat an imported file as a new unsaved diagram so auto-save creates
          // a fresh one instead of overwriting whatever was open.
          setCurrentDiagramId(null);
          loadedDiagramIdRef.current = null;
          setCurrentDiagramName('Imported Diagram');
          setIsModified(true);
          trackDiagramImported(loadedNodes.length, loadedEdges.length);
          
          setTimeout(() => reactFlowInstance?.fitView({ duration: 800 }), 100);
        }
      } catch (error) {
        console.error('Failed to load flow', error);
        toast.error("That file isn't a valid diagram JSON.");
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const triggerLoadLocal = () => fileInputRef.current?.click();

  // --- Context Menu Actions ---
  const ctxNode = ctxTarget.type === 'node' ? ctxTarget.node : null;
  const ctxEdge = ctxTarget.type === 'edge' ? ctxTarget.edge : null;

  // Delete every selected node. Detaches children of any deleted group (so they
  // survive) and drops edges that touch a removed node. The keyboard delete
  // path (onNodesChangeWithHistory + planGroupDetach) mirrors this behavior.
  const deleteSelectedNodes = useCallback(() => {
    const removedIds = new Set(nodesRef.current.filter((n) => n.selected).map((n) => n.id));
    if (removedIds.size === 0) return;
    triggerSnapshot();
    const selectedGroupIds = new Set(
      nodesRef.current.filter((n) => n.selected && n.type === 'groupNode').map((n) => n.id)
    );
    setNodes((nds) =>
      detachChildrenOfRemovedGroups(nds, selectedGroupIds).filter((n) => !n.selected)
    );
    setEdges((eds) => eds.filter((e) => !removedIds.has(e.source) && !removedIds.has(e.target)));
  }, [setNodes, setEdges, triggerSnapshot]);

  // Copy the right-clicked node's label.
  const ctxCopyName = useCallback(() => {
    if (!ctxNode) return;
    navigator.clipboard?.writeText(ctxNode.data?.label ?? '');
    toast.success('Name copied');
  }, [ctxNode, toast]);

  // Lock / unlock the right-clicked node.
  const ctxToggleLock = useCallback(() => {
    if (!ctxNode) return;
    triggerSnapshot();
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== ctxNode.id) return n;
        const locked = n.data?.locked ?? false;
        return { ...n, draggable: locked, data: { ...n.data, locked: !locked } };
      })
    );
  }, [ctxNode, setNodes, triggerSnapshot]);

  // Recolor the right-clicked shape node.
  const ctxChangeShapeColor = useCallback((fillColor: string, borderColor: string) => {
    if (!ctxNode) return;
    triggerSnapshot();
    setNodes((nds) =>
      nds.map((n) =>
        n.id === ctxNode.id && n.type === 'shapeNode'
          ? { ...n, data: { ...n.data, fillColor, borderColor } }
          : n
      )
    );
  }, [ctxNode, setNodes, triggerSnapshot]);

  // Get count of selected nodes (toolbar align/distribute + context menu)
  const selectedCount = nodes.filter((n) => n.selected).length;

  // Actions surfaced by the right-click context menu (canvas / node / edge / selection).
  const ctxActions: CtxActions = {
    addNode: () => {
      pendingAddPosRef.current = { ...ctxPosRef.current };
      setCommandPaletteMode('add');
      setIsCommandPaletteOpen(true);
    },
    paste: () => {
      if (!reactFlowInstance) return;
      pasteClipboard(reactFlowInstance.screenToFlowPosition({ x: ctxPosRef.current.x, y: ctxPosRef.current.y }));
    },
    selectAll: () => setNodes((nds) => nds.map((n) => ({ ...n, selected: true }))),
    layout: (dir) => handleLayout(dir),
    fitView: () => reactFlowInstance?.fitView({ duration: 500, padding: 0.2 }),
    toggleGrid: () => setShowGrid((v) => !v),
    importJson: triggerLoadLocal,
    importMermaid: () => {
      setMermaidError(null);
      setIsMermaidModalOpen(true);
    },
    clear: () => { void clearCanvas(); },
    duplicate: handleDuplicateSelected,
    // A single-node menu restacks that node; a selection menu restacks them all.
    reorder: (direction) => handleReorder(direction, ctxNode?.id),
    copyName: ctxCopyName,
    changeIcon: () => { if (ctxNode) openIconPicker(ctxNode.id); },
    toggleLock: ctxToggleLock,
    changeColor: ctxChangeShapeColor,
    ungroup: () => { if (ctxNode) handleUngroup(ctxNode.id); },
    group: handleGroupSelected,
    align: handleAlign,
    distribute: handleDistribute,
    deleteSelection: deleteSelectedNodes,
    editEdge: () => { if (ctxEdge) { setSelectedEdgeId(ctxEdge.id); setSelectedNodeId(null); } },
    setEdgeType: (type) => { if (ctxEdge) handleEdgeUpdate(ctxEdge.id, { type }); },
    setEdgeArrows: (mode) => {
      if (!ctxEdge) return;
      const color = (ctxEdge.style?.stroke as string) || '#64748b';
      handleEdgeUpdate(ctxEdge.id, {
        markerEnd: mode === 'none' ? undefined : { type: MarkerType.ArrowClosed, color },
        markerStart: mode === 'both' ? { type: MarkerType.ArrowClosed, color } : undefined,
      });
    },
    toggleEdgeAnim: () => { if (ctxEdge) handleEdgeUpdate(ctxEdge.id, { animated: !ctxEdge.animated }); },
    resetEdgeLabel: () => {
      if (ctxEdge) handleEdgeUpdate(ctxEdge.id, { data: { ...ctxEdge.data, labelT: undefined } });
    },
    deleteEdge: () => { if (ctxEdge) handleEdgeDelete(ctxEdge.id); },
  };

  // Save status indicator component

  // Show loading state while auth is loading or initial diagram is loading
  if (isAuthLoading || isInitialLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-surface-canvas">
        <BrandLoader label="Loading diagram…" />
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-hidden relative flex">
      {/* Demo mode: a slim signup strip; the editor itself is the real thing. */}
      {demo && (
        <div className="fixed left-0 right-0 top-0 z-[60] flex items-center justify-center gap-3 border-b border-border bg-surface-secondary/90 px-4 py-1.5 text-xs font-medium backdrop-blur-sm">
          <span className="text-muted-foreground">You're exploring the demo — changes aren't saved.</span>
          <a
            href="/auth"
            className="ss-press rounded-md bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            Sign up free
          </a>
        </div>
      )}
      {/* Main Content */}
      <div className="flex-1 relative">
        <ContextMenu>
          <ContextMenuTrigger>
            <div
              className="w-full h-full absolute inset-0 transition-colors duration-200 bg-surface-canvas"
              ref={reactFlowWrapper}
            >
          {/* The demo edits freely, it just never persists. */}
          <EdgeLabelProvider editable onSnapshot={triggerSnapshot}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChangeWithHistory}
            onEdgesChange={onEdgesChangeWithHistory}
            onConnect={onConnect}
            onEdgeUpdate={onEdgeUpdate}
            onEdgeUpdateStart={onEdgeUpdateStart}
            onEdgeUpdateEnd={onEdgeUpdateEnd}
            edgeUpdaterRadius={16}
            onInit={setReactFlowInstance}
            onNodeDragStart={onNodeDragStart}
            onNodeDragStop={onNodeDragStop}
            onSelectionDragStart={onSelectionDragStart}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            onNodeClick={onNodeClick}
            onNodeContextMenu={onNodeContextMenu}
            onPaneContextMenu={onPaneContextMenu}
            onEdgeContextMenu={onEdgeContextMenu}
            onSelectionContextMenu={onSelectionContextMenu}
            nodeTypes={nodeTypes}
            fitView
            snapToGrid={snapToGrid} // user pref (default off — alignment guides handle spacing)
            snapGrid={[20, 20]}
            selectionOnDrag
            selectionMode={SelectionMode.Partial}
            panOnDrag={PAN_ON_DRAG} // middle/right-drag pans; left-drag rubber-bands
            panOnScroll // scroll/trackpad pans, Ctrl/Cmd+scroll & pinch zoom
            multiSelectionKeyCode={MULTI_SELECT_KEYS} // Shift/Ctrl/Cmd + click adds to selection
            deleteKeyCode={DELETE_KEYS} // Delete & Backspace remove selection (+ connected edges)
            connectionMode={ConnectionMode.Loose} // Allow handles to act as both source and target
            // Selection must not change stacking, or "send to back" would look
            // like a no-op until the node is deselected.
            elevateNodesOnSelect={false}
            className="transition-colors duration-200 bg-surface-canvas"
            edgeTypes={EDGE_TYPES}
            // Labels are rendered by our own edge components (see EDGE_TYPES),
            // so React Flow's labelStyle/labelBg* props no longer apply.
            defaultEdgeOptions={{
              type: 'smoothstep',
              style: { strokeWidth: 2 },
            }}
            proOptions={{ hideAttribution: true }}
          >
            {showGrid && (
              <Background
                color={isDark ? 'var(--border)' : 'var(--border)'}
                gap={16}
                size={2}
                variant={BackgroundVariant.Dots}
              />
            )}
            <HelperLines horizontal={helperLineHorizontal} vertical={helperLineVertical} />
            <Controls className="!bg-surface-secondary !border-border !fill-muted-foreground [&>button]:!border-b-border hover:[&>button]:!bg-surface-tertiary dark:!border-border dark:!fill-muted-foreground dark:[&>button]:!border-b-border dark:hover:[&>button]:!bg-surface-tertiary" />
            {showMinimap && (
              <MiniMap
                pannable
                zoomable
                nodeColor="var(--muted-foreground)"
                maskColor="color-mix(in srgb, var(--background) 82%, transparent)"
                className="!bottom-16 !bg-card !border !border-border !rounded-md"
              />
            )}

            {/* Blank-canvas hint */}
            {nodes.length === 0 && !isInitialLoading && (
              <Panel position="top-center" className="pointer-events-none select-none">
                <div className="mt-[34vh] flex flex-col items-center gap-1.5 text-center">
                  <div className="text-[15px] font-semibold text-foreground/80">Your canvas is empty</div>
                  <div className="text-[12.5px] text-muted-foreground">
                    Press{' '}
                    <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[11px]">⌘K</kbd>{' '}
                    to add a node, or use the toolbar below.
                  </div>
                </div>
              </Panel>
            )}

            {/* Top Center - Align & Distribute (multi-selection) */}
            {selectedCount >= 2 && (
              <Panel position="top-center">
                <div className="flex items-center gap-0.5 bg-surface-secondary border border-border rounded-lg shadow-lg p-1">
                  <Tooltip content="Align left" side="bottom">
                    <button
                      onClick={() => handleAlign('left')}
                      className="flex items-center justify-center p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-muted transition-all ss-press"
                      aria-label="Align left"
                    >
                      <AlignStartVertical className="w-4 h-4" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Align horizontal centers" side="bottom">
                    <button
                      onClick={() => handleAlign('center-h')}
                      className="flex items-center justify-center p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-muted transition-all ss-press"
                      aria-label="Align horizontal centers"
                    >
                      <AlignCenterVertical className="w-4 h-4" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Align right" side="bottom">
                    <button
                      onClick={() => handleAlign('right')}
                      className="flex items-center justify-center p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-muted transition-all ss-press"
                      aria-label="Align right"
                    >
                      <AlignEndVertical className="w-4 h-4" />
                    </button>
                  </Tooltip>
                  <div className="w-px h-5 bg-border mx-0.5" />
                  <Tooltip content="Align top" side="bottom">
                    <button
                      onClick={() => handleAlign('top')}
                      className="flex items-center justify-center p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-muted transition-all ss-press"
                      aria-label="Align top"
                    >
                      <AlignStartHorizontal className="w-4 h-4" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Align vertical centers" side="bottom">
                    <button
                      onClick={() => handleAlign('center-v')}
                      className="flex items-center justify-center p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-muted transition-all ss-press"
                      aria-label="Align vertical centers"
                    >
                      <AlignCenterHorizontal className="w-4 h-4" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Align bottom" side="bottom">
                    <button
                      onClick={() => handleAlign('bottom')}
                      className="flex items-center justify-center p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-muted transition-all ss-press"
                      aria-label="Align bottom"
                    >
                      <AlignEndHorizontal className="w-4 h-4" />
                    </button>
                  </Tooltip>
                  {selectedCount >= 3 && (
                    <>
                      <div className="w-px h-5 bg-border mx-0.5" />
                      <Tooltip content="Distribute horizontally" side="bottom">
                        <button
                          onClick={() => handleDistribute('horizontal')}
                          className="flex items-center justify-center p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-muted transition-all ss-press"
                          aria-label="Distribute horizontally"
                        >
                          <AlignHorizontalDistributeCenter className="w-4 h-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content="Distribute vertically" side="bottom">
                        <button
                          onClick={() => handleDistribute('vertical')}
                          className="flex items-center justify-center p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-muted transition-all ss-press"
                          aria-label="Distribute vertically"
                        >
                          <AlignVerticalDistributeCenter className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    </>
                  )}
                </div>
              </Panel>
            )}

            {/* Top Left - demo branding only. The diagram's icon, name and save
                state live in the tab strip above the editor. */}
            {demo && (
              <Panel position="top-left">
                <img src="/StackSketch-IconLogo.svg" alt="StackSketch" className="mt-9 h-8 w-auto" />
              </Panel>
            )}

            {/* Top Right - Share, Export and Profile */}
            <Panel position="top-right">
              <div className={`flex gap-2 items-center ${demo ? 'mt-9' : ''}`}>
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <button
                      className={`ss-press flex h-7 items-center gap-1 rounded-md border px-2 text-[12px] font-medium shadow-sm transition-all data-[state=open]:bg-muted ${
                        isPublic
                          ? 'border-emerald-300 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-400 dark:hover:bg-emerald-900'
                          : 'border-border bg-card text-foreground hover:bg-muted'
                      }`}
                      aria-label="Share & export"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Share</span>
                      <ChevronDown className="w-3 h-3 opacity-70" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    {!demo && (
                      <>
                        <DropdownMenuItem
                          disabled={!isAuthenticated}
                          onSelect={() => {
                            if (!currentDiagramId) {
                              toast.info('Add something to the canvas first, then you can share it.');
                              return;
                            }
                            setIsShareModalOpen(true);
                          }}
                        >
                          <Share2 className="h-4 w-4" />
                          {isPublic ? 'Share link (public)…' : 'Share link…'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onSelect={() => handleExport('png')}>
                      <ImageIcon className="h-4 w-4" />
                      Export PNG
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => {
                        if (!canExportFormat('svg')) {
                          setShowExportUpgrade('export-svg');
                        } else {
                          handleExport('svg');
                        }
                      }}
                    >
                      <FileJson className="h-4 w-4" />
                      Export SVG
                      {!canExportFormat('svg') && <Crown className="ml-auto h-3.5 w-3.5 text-amber-500" />}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={handleSaveLocal}>
                      <Save className="h-4 w-4" />
                      Export JSON
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </Panel>

            {/* Bottom Center - Persistent toolbar. Only canvas-wide actions that
                are always available live here. Insert/Layout/View expand their
                section inline inside the same track; selection-scoped tools will
                share the pattern. Document actions (share, export) live on the
                top-right menu. */}
            <Panel position="bottom-center">
              <div ref={toolbarsRef} className="flex flex-col items-center gap-2">
                <MotionConfig reducedMotion="user">
                <div className="flex items-center gap-0.5 max-w-[calc(100vw-1rem)] overflow-x-auto border p-1 rounded-full shadow-xl bg-surface-secondary border-border" role="toolbar" aria-label="Editor tools">
                  {/* History */}
                  <ToolButton label="Undo (Ctrl+Z)" onClick={handleUndo} disabled={!canUndo}>
                    <ArrowCounterClockwise className="w-5 h-5" weight="fill" />
                  </ToolButton>
                  <ToolButton label="Redo (Ctrl+Shift+Z)" onClick={handleRedo} disabled={!canRedo}>
                    <ArrowClockwise className="w-5 h-5" weight="fill" />
                  </ToolButton>

                  {/* Create */}
                  <ToolButton
                    label="Add node (Ctrl+K)"
                    className="gap-1.5 px-3"
                    onClick={() => { setCommandPaletteMode('add'); setIsCommandPaletteOpen(true); }}
                  >
                    <MagnifyingGlass className="w-4 h-4" weight="fill" />
                    <span className="text-[12.5px] font-medium">Add node</span>
                  </ToolButton>
                  <ToolButton
                    label={demo ? 'Sign up to generate with AI' : 'AI Assistant (Ctrl+J)'}
                    tone="ai"
                    className="gap-1.5 px-3"
                    onClick={handleToggleAIPanel}
                  >
                    <Sparkle className="w-4 h-4" weight="fill" />
                    <span className="text-[12.5px] font-medium">Generate</span>
                  </ToolButton>

                  {/* Sub-bar triggers — each expands its section inline, inside
                      this same track, instead of floating a second bar above. */}
                  <ToolButton
                    ref={insertBtnRef}
                    label="Insert"
                    kind="menu"
                    className="gap-1.5 px-3"
                    expanded={openSubBar === 'insert'}
                    active={openSubBar === 'insert'}
                    onClick={() => toggleSubBar('insert')}
                  >
                    <Plus className="w-4 h-4" weight="fill" />
                    <span className="text-[12.5px] font-medium">Insert</span>
                  </ToolButton>
                  <AnimatePresence initial={false}>
                    {openSubBar === 'insert' && (
                      <SubBar inline key="insert" label="Insert" onClose={closeSubBar} boundaryRef={toolbarsRef} returnFocusRef={insertBtnRef}>
                      <ToolButton label="Text" className="gap-1.5 px-3" onClick={() => { handleAddTextNode(); closeSubBar(); }}>
                        <TextT className="w-4 h-4" weight="fill" />
                        <span className="text-[12.5px] font-medium">Text</span>
                      </ToolButton>
                      <ToolButton label="Sticky note" className="gap-1.5 px-3" onClick={() => { handleAddStickyNote(); closeSubBar(); }}>
                        <Note className="w-4 h-4" weight="fill" />
                        <span className="text-[12.5px] font-medium">Sticky note</span>
                      </ToolButton>
                      <div className="flex gap-0.5 border-l border-border pl-1.5 ml-0.5">
                        {INSERT_SHAPES.map(({ type, label, Icon }) => (
                          <ToolButton key={type} label={label} onClick={() => { handleAddShapeNode(type); closeSubBar(); }}>
                            <Icon className={`w-5 h-5 ${SHAPE_ICON_COLOR[type]}`} />
                          </ToolButton>
                        ))}
                      </div>
                    </SubBar>
                    )}
                  </AnimatePresence>

                  <ToolButton
                    ref={layoutBtnRef}
                    label="Auto-layout"
                    kind="menu"
                    className="gap-1.5 px-3"
                    expanded={openSubBar === 'layout'}
                    active={openSubBar === 'layout'}
                    onClick={() => toggleSubBar('layout')}
                  >
                    <TreeStructure className="w-4 h-4" weight="fill" />
                    <span className="text-[12.5px] font-medium">Layout</span>
                  </ToolButton>
                  <AnimatePresence initial={false}>
                    {openSubBar === 'layout' && (
                      <SubBar inline key="layout" label="Auto-layout" onClose={closeSubBar} boundaryRef={toolbarsRef} returnFocusRef={layoutBtnRef}>
                      <ToolButton label="Auto-layout vertical" className="gap-1.5 px-3" onClick={() => { handleLayout('TB'); closeSubBar(); }}>
                        <TreeStructure className="w-4 h-4" weight="fill" />
                        <span className="text-[12.5px] font-medium">Vertical</span>
                      </ToolButton>
                      <ToolButton label="Auto-layout horizontal" className="gap-1.5 px-3" onClick={() => { handleLayout('LR'); closeSubBar(); }}>
                        <TreeStructure className="w-4 h-4 rotate-90" weight="fill" />
                        <span className="text-[12.5px] font-medium">Horizontal</span>
                      </ToolButton>
                    </SubBar>
                    )}
                  </AnimatePresence>

                  <ToolButton
                    ref={viewBtnRef}
                    label="View"
                    kind="menu"
                    className="gap-1.5 px-3"
                    expanded={openSubBar === 'view'}
                    active={openSubBar === 'view'}
                    onClick={() => toggleSubBar('view')}
                  >
                    <Eye className="w-4 h-4" weight="fill" />
                    <span className="text-[12.5px] font-medium">View</span>
                  </ToolButton>
                  <AnimatePresence initial={false}>
                    {openSubBar === 'view' && (
                      <SubBar inline key="view" label="View" onClose={closeSubBar} boundaryRef={toolbarsRef} returnFocusRef={viewBtnRef}>
                      {/* Toggles stay open so several can be flipped in one visit. */}
                      <ToolButton label="Dot grid" kind="toggle" className="gap-1.5 px-3" active={showGrid} onClick={() => setShowGrid((v) => !v)}>
                        <GridNine className="w-4 h-4" weight="fill" />
                        <span className="text-[12.5px] font-medium">Grid</span>
                      </ToolButton>
                      <ToolButton label="Snap to grid" kind="toggle" className="gap-1.5 px-3" active={snapToGrid} onClick={() => setSnapToGrid((v) => !v)}>
                        <Magnet className="w-4 h-4" weight="fill" />
                        <span className="text-[12.5px] font-medium">Snap</span>
                      </ToolButton>
                      <ToolButton label="Minimap" kind="toggle" className="gap-1.5 px-3" active={showMinimap} onClick={() => setShowMinimap((v) => !v)}>
                        <MapTrifold className="w-4 h-4" weight="fill" />
                        <span className="text-[12.5px] font-medium">Minimap</span>
                      </ToolButton>
                      <div className="flex gap-0.5 border-l border-border pl-1.5 ml-0.5">
                        <ToolButton label="Fit view" className="gap-1.5 px-3" onClick={() => { reactFlowInstance?.fitView({ duration: 500, padding: 0.2 }); closeSubBar(); }}>
                          <ArrowsOut className="w-4 h-4" weight="fill" />
                          <span className="text-[12.5px] font-medium">Fit</span>
                        </ToolButton>
                        <ToolButton label="Zoom to 100%" className="gap-1.5 px-3" onClick={() => { reactFlowInstance?.zoomTo(1, { duration: 300 }); closeSubBar(); }}>
                          <Crosshair className="w-4 h-4" weight="fill" />
                          <span className="text-[12.5px] font-medium">100%</span>
                        </ToolButton>
                      </div>
                    </SubBar>
                    )}
                  </AnimatePresence>
                </div>
                </MotionConfig>
              </div>
            </Panel>

            {/* Bottom Right - Help Button */}
            <Panel position="bottom-right">
              <KeyboardCheatSheet variant="icon" />
            </Panel>

            {/* Hidden file input for local load */}
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".json"
              onChange={handleLoadLocal}
            />

            {selectedEdge && (
              <EdgeSettingsPanel
                edge={selectedEdge}
                onUpdate={handleEdgeUpdate}
                onDelete={handleEdgeDelete}
                onClose={() => setSelectedEdgeId(null)}
              />
            )}

            {selectedNode && !selectedEdge && (
              <NodeSettingsPanel
                node={selectedNode}
                onUpdate={handleNodeUpdate}
                onDelete={handleNodeDelete}
                onClose={() => setSelectedNodeId(null)}
                onChangeIcon={() => openIconPicker(selectedNode.id)}
              />
            )}

          </ReactFlow>
          </EdgeLabelProvider>
            </div>
          </ContextMenuTrigger>
          <EditorContextMenu
            target={ctxTarget}
            showGrid={showGrid}
            canPaste={clipboardRef.current.length > 0}
            actions={ctxActions}
          />
        </ContextMenu>
      </div>

      <AIPanel
        open={isAIPanelOpen}
        container={aiDock?.slot ?? null}
        onClose={() => setIsAIPanelOpen(false)}
        messages={chatMessages}
        isGenerating={isGenerating}
        hasDiagram={nodes.length > 0}
        onSend={handleChatSend}
        onStop={cancelAIActivity}
        onNewChat={handleAINewChat}
        onRetry={(m) =>
          m.retryPayload &&
          handleChatSend(
            m.retryPayload.instruction,
            m.retryPayload.provider,
            m.retryPayload.modelId,
            m.id
          )
        }
      />

      <MermaidImportModal
        isOpen={isMermaidModalOpen}
        onClose={() => {
          setIsMermaidModalOpen(false);
          setMermaidError(null);
        }}
        onImport={handleImportMermaid}
        error={mermaidError}
        hasExistingNodes={nodes.length > 0}
      />

      {/* AI Configuration Prompt */}
      {showAIConfigPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className={`rounded-2xl w-full max-w-md overflow-hidden bg-surface-secondary border border-border`}>
            <div className={`flex items-center justify-between p-6 border-b border-border`}>
              <div className="flex items-center gap-2">
                <div className="p-2 bg-amber-500/10 rounded-lg">
                  <Settings className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${isDark ? 'text-foreground' : 'text-foreground'}`}>
                    AI Not Configured
                  </h3>
                  <p className={`text-xs ${isDark ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                    Add your API key to use AI features
                  </p>
                </div>
              </div>
            </div>
            
            <div className={`p-6 bg-surface-secondary`}>
              <p className={`text-sm mb-6 ${isDark ? 'text-foreground' : 'text-foreground'}`}>
                To generate diagrams with AI, add an API key for any supported provider — Gemini, OpenAI, Anthropic, or an OpenAI-compatible endpoint — in the Settings page.
                Your API key is stored locally and encrypted - it never leaves your browser.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAIConfigPrompt(false)}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                    isDark
                      ? 'bg-surface-tertiary text-foreground hover:bg-surface-primary'
                      : 'bg-surface-tertiary text-foreground hover:bg-surface-primary'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowAIConfigPrompt(false);
                    navigate('/settings');
                  }}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-primary to-primary text-primary-foreground text-sm font-semibold rounded-xl hover:from-primary/90 hover:to-primary/90 transition-all flex items-center justify-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Go to Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
        onSelect={(icon) => {
          if (commandPaletteMode === 'changeIcon' && iconChangeNodeId) {
            handleChangeNodeIcon(iconChangeNodeId, icon);
          } else {
            handleAddNode(icon);
          }
        }}
        isDark={isDark}
      />

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />

      {/* Export Upgrade Prompt */}
      {showExportUpgrade && (
        <UpgradePrompt
          isOpen={!!showExportUpgrade}
          onClose={() => setShowExportUpgrade(null)}
          feature={showExportUpgrade}
        />
      )}

      {/* Share Modal */}
      {currentDiagramId && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          diagramId={currentDiagramId}
          diagramName={currentDiagramName}
          initialIsPublic={isPublic}
          initialShareToken={shareToken}
          onShareUpdate={(data) => {
            setIsPublic(data.isPublic);
            setShareToken(data.shareToken);
          }}
        />
      )}

      <OnboardingModal />
    </div>
  );
};

const FlowEditor: React.FC<FlowEditorProps> = (props) => (
  <ReactFlowProvider>
    <FlowEditorContent {...props} />
  </ReactFlowProvider>
);

export default FlowEditor;
